/**
 * Main Application Component
 * 
 * This is the root component of the American Police Training application. It sets up
 * routing, authentication context, and renders all the main pages and components.
 * Handles lazy loading, error boundaries, and global application state.
 * 
 * @author Andres Nava
 * @version 1.0.0
 */

/// <reference types="vite/client" />

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { shouldMountStudioPublicRouter } from '../shared/studio/studioHostUtils';
import { shouldMountAmeriConPublicRouter } from '../shared/americon/americonHostUtils';
import { isLocalDevHostname } from '../shared/tenant/hostUtils';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Outlet, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Typography, Button, Alert } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProviderAccessBanner from './components/ProviderAccessBanner';
import TrainingAlertsNudgeManager from './components/training-alerts/TrainingAlertsNudgeManager';
import UnifiedHelpSpeedDial from './components/common/UnifiedHelpSpeedDial';
import SpeedDialErrorBoundary from './components/common/SpeedDialErrorBoundary';
import { useBugReportHandler } from './components/common/FloatingBugReportButton';
import BugReportDialog from './components/common/BugReportDialog';
import { logger } from "./utils/logger";
import { securityMonitor } from './utils/securityMonitor';
import { applyCanonicalViewportMeta } from './constants/viewport';
import { AGENCY_SHELL_HORIZONTAL_PX } from './constants/layoutSpacing';
import { PUBLIC_SHELL_MIN_HEIGHT } from './constants/clsShell';
import { K9ChromeProvider } from '../k9/components/layout/K9ChromeContext';
import K9SubdomainEntryRedirect from '../k9/components/routing/K9SubdomainEntryRedirect';
import { safeRouterPathname, safeRouterSearch } from './utils/safeNavigation';
import { isViteFlagEnabled } from './utils/runtimePlatformFlags';
import { useDropdownBackdropSafetyNet } from './hooks/useDropdownBackdropSafetyNet';

// Platform Page Wrapper - adds Speed Dial and Bug Report to pages not using Layout component
const PlatformPageWrapper: React.FC<{ children: React.ReactNode; showTours?: boolean }> = ({ children, showTours = true }) => {
  const bugReportHandler = useBugReportHandler();
  const location = useLocation();
  const subdomainInfo = detectSubdomain();
  const isHomePage = location.pathname === '/';
  // On DASH subdomain, the root path is the dashboard, so always show tours
  const isDashSubdomain = subdomainInfo.subdomain === SUBDOMAINS.DASH;
  const shouldShowTours = showTours && (!isHomePage || isDashSubdomain);

  return (
    <>
      {children}
      
      {/* Bug Report Dialog */}
      <BugReportDialog 
        open={bugReportHandler.dialogOpen} 
        onClose={bugReportHandler.handleClose} 
        getCapturedLogs={bugReportHandler.getCapturedLogs} 
        initialScreenshot={bugReportHandler.capturedScreenshot}
      />
      
      {/* Unified Help Speed Dial - Show on all platform pages */}
      <SpeedDialErrorBoundary>
        <UnifiedHelpSpeedDial
          onBugReport={bugReportHandler.handleOpen}
          showBugReport={true}
          showChat={true}
          showTours={shouldShowTours}
        />
      </SpeedDialErrorBoundary>
    </>
  );
};

/** Outer column shell for listed public subdomains: fills `.app` flex area (see index.css) instead of a second viewport min-height. */
const listedPublicSubdomainOuterSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  backgroundColor: 'background.default',
} as const;

/** Apex short paths (`/lesson-plans/*`, `/exams/*`, …) → `/tacticore/*`. Path validated to avoid open redirects. */
function RedirectShortPathToTacticoreApp() {
  const location = useLocation();
  useEffect(() => {
    const pathname = safeRouterPathname(location.pathname);
    const search = safeRouterSearch(location.search ?? '');
    if (pathname.includes('..') || pathname.includes('\\')) {
      window.location.assign(getTacticoreUrl());
      return;
    }
    window.location.assign(`${getMainSiteUrl()}${getTacticoreAppPath(pathname)}${search}`);
  }, [location.pathname, location.search]);
  return <PageLoader />;
}

/** Apex short paths `/manage/:id/edit|preview` → `/blog/manage/:id/...` */
function RedirectBlogManagePost(suffix: 'edit' | 'preview') {
  return function RedirectBlogManagePostInner() {
    const { id } = useParams();
    const safeId = String(id || '').replace(/[^\d]/g, '');
    if (!safeId) {
      return <Navigate to="/blog/manage" replace />;
    }
    return <Navigate to={`/blog/manage/${safeId}/${suffix}`} replace />;
  };
}
const RedirectBlogManageEdit = RedirectBlogManagePost('edit');
const RedirectBlogManagePreview = RedirectBlogManagePost('preview');

// Ensure React is available globally for Material-UI
if (typeof window !== 'undefined' && !(window as any).React) {
  (window as any).React = React;
}
// Using Google Analytics for tracking
import { trackPageView, trackEvent } from './utils/analytics';
import GoogleAnalytics from './components/analytics/GoogleAnalytics';
import NewRelicScript from './components/NewRelicScript';
import PopupAdvertisement from './components/common/PopupAdvertisement';
import NotFoundModal from './components/common/NotFoundModal';
import ScrollToTopOnNavigate from './components/routing/ScrollToTopOnNavigate';
import { BackendUnavailableBanner } from './components/BackendUnavailableBanner';
import SEOHead from './components/seo/SEOHead';
import HybridConsentManager from './components/legal/HybridConsentManager';
import GoogleAdSenseLoader from './components/GoogleAdSenseLoader';
import OverlayAdCoordinatorProvider from './ads/OverlayAdCoordinator';
import CookieTrackingProvider from './components/analytics/CookieTrackingProvider';
// Eager homepage — avoid lazy chunk delay before hero LCP on `/`
import HomePage from './pages/HomePage';
// AdBlockingDiagnostics now controlled via UnifiedHelpSpeedDial component (admin only, dev mode only)
// import AdBlockingDiagnostics from './components/debug/AdBlockingDiagnostics';
import { MuiThemeContextProvider } from './contexts/MuiThemeContext';
import ReactProvider from './components/common/ReactProvider';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider } from './contexts/auth/authContext';
import { LearningLabProvider } from './contexts/LearningLabContext';
import LearningLabBanner from './components/onboarding/LearningLabBanner';
import DashboardModuleErrorBoundary from './components/dashboard/DashboardModuleErrorBoundary';
import { OnboardingProvider as OnboardingProviderWrapper } from './components/onboarding';
import onboardingTours from './config/onboardingTours';
import { ConditionalChatProvider } from './components/common/ConditionalChatProvider';
import DeferredSupportChatWidget from './components/common/DeferredSupportChatWidget';
import ImpersonationBanner from './components/common/ImpersonationBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { registerGlobalClientErrorHandlers } from './utils/reportClientIncident';
import { registerBrowserCsrfLifecycleHandlers } from './utils/apiClient';
import K9ErrorFallback from '../k9/components/common/K9ErrorFallback';
import { useAccessPolicies } from './hooks/useAccessPolicies';
import { useExtensionCompatibility } from './hooks/useExtensionCompatibility';
import { Favicon } from './components/common';
import { safeLazy } from './utils/safeLazy';
import './utils/impersonationUtils'; // Import impersonation utilities
// Debug utilities removed for security - use feature flags if needed
import { Provider as ReduxProvider } from 'react-redux';
import store from './store';
import { isSessionAuthenticated, getUserData } from './utils/storageUtils';
import { safeStorage } from './utils/trackingUtils';
import { initializeTrackingPrevention } from './utils/trackingPreventionInit';

// Import logger for production-safe logging
// import { logger } from "./utils/logger";
// Import banner test utilities in development
if (import.meta.env.DEV) {
  import('./utils/bannerTestUtils');
}
// Debug utilities removed for security - use feature flags if needed
import { suppressBrowserExtensionErrorsTemporarily } from './utils/errorSuppression';
import {
  detectSubdomain,
  SUBDOMAINS,
  getNavigationUrl,
  getTacticoreUrl,
  getTacticoreAppPath,
  getMainSiteUrl,
} from './utils/subdomainUtils';
import { stripBackofficePrefix } from './pages/backoffice/backofficeRoutePaths';

// Shell stays sync; public/auth pages are lazy so homepage main chunk stays lean (PSI unused JS).
import Layout from './components/layout/Layout';
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AgentClaimPage = lazy(() => import('./pages/AgentClaimPage'));
import OfficeaptAuthBridgePage from './pages/OfficeaptAuthBridgePage';
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const LogoutConfirmationPage = lazy(() => import('./pages/LogoutConfirmationPage'));
const MfaVerifyPage = lazy(() => import('./pages/MfaVerifyPage'));
const MfaSetupPage = lazy(() => import('./pages/MfaSetupPage'));
const Setup2FAPage = lazy(() => import('./pages/Setup2FAPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const TrainingAlertsLandingPage = lazy(() => import('./pages/TrainingAlertsLandingPage'));
const ForOfficersLandingPage = lazy(() => import('./pages/ForOfficersLandingPage'));
const ForProfessionalsLandingPage = lazy(() => import('./pages/ForProfessionalsLandingPage'));
const WriteForAptLandingPage = lazy(() => import('./pages/WriteForAptLandingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TicsStandardsPage = lazy(() => import('./pages/standards/TicsStandardsPage'));
const TicsWhitePaperPage = lazy(() => import('./pages/standards/TicsWhitePaperPage'));
const TicsSpecificationOverviewPage = lazy(
  () => import('./pages/standards/TicsSpecificationOverviewPage'),
);
const FAQPage = lazy(() => import('./pages/FAQPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const GDPRCompliancePage = lazy(() => import('./pages/GDPRCompliancePage'));
const DataRequestPage = lazy(() => import('./pages/DataRequestPage'));
import ProtectedRoute from './components/auth/ProtectedRoute';
const MeRoutes = lazy(() => import('./routes/me'));
const TeachRoutes = lazy(() => import('./routes/teach'));
const ChatRoutes = lazy(() => import('./routes/chat'));
import { SessionExpiredProvider } from './contexts/SessionExpiredContext';
import SessionExpiredMessage from './components/auth/SessionExpiredMessage';
import { SessionWarningProvider } from './contexts/SessionWarningContext';
import SessionWarningModal from './components/auth/SessionWarningModal';
import StudioAmericonDashboardLaunchGate from './components/dashboard/StudioAmericonDashboardLaunchGate';

/** Studio dashboard routes: auth + prod Coming Soon until VITE_APT_STUDIO_PROD_PUBLISHED. */
const AptStudioLaunchProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <StudioAmericonDashboardLaunchGate product="apt_studio">{children}</StudioAmericonDashboardLaunchGate>
  </ProtectedRoute>
);

/** AmeriCon dashboard routes: auth + prod Coming Soon until VITE_AMERICON_PROD_PUBLISHED. */
const AmeriConLaunchProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <StudioAmericonDashboardLaunchGate product="americon">{children}</StudioAmericonDashboardLaunchGate>
  </ProtectedRoute>
);

// PWA Components
import { DashPWAManager } from './components/pwa/DashPWAManager';

// Lazy load heavy admin and dashboard pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BackofficePage = lazy(() => import('./pages/backoffice/BackofficePage'));
const HealthcareAdminPage = lazy(() => import('./pages/admin/HealthcareAdminPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DashboardSettingsPage = lazy(() => import('./pages/DashboardSettingsPage'));
const AgencyOfficeSubmissionsPage = lazy(() => import('./pages/coordinator/AgencyOfficeSubmissionsPage'));
const AgencyTrainingPlanPage = lazy(() => import('./pages/coordinator/AgencyTrainingPlanPage'));
const PremiumPage = lazy(() => import('./pages/PremiumPage'));
const SubscriptionSettingsPage = lazy(() => import('./pages/SubscriptionSettingsPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const TacShopPage = lazy(() => import('./pages/TacShopPage'));
const SecondaryEmploymentHomePage = lazy(() => import('./pages/secondary-employment/SecondaryEmploymentHomePage'));
const SecondaryEmploymentForEmployersPage = lazy(() => import('./pages/secondary-employment/SecondaryEmploymentForEmployersPage'));
const SecondaryEmploymentListingDetailPage = lazy(() => import('./pages/secondary-employment/SecondaryEmploymentListingDetailPage'));
const SecondaryEmploymentEmployerPublicPage = lazy(() => import('./pages/secondary-employment/SecondaryEmploymentEmployerPublicPage'));
const SecondaryEmploymentDashboardPage = lazy(() => import('./pages/secondary-employment/SecondaryEmploymentDashboardPage'));
const AgencyRecruitingJobDetailPage = lazy(() => import('./pages/AgencyRecruitingJobDetailPage'));
const TacShopProductDetailPage = lazy(() => import('./pages/TacShopProductDetailPage'));
const TacShopCartPage = lazy(() => import('./pages/TacShopCartPage'));
const TacShopCheckoutPage = lazy(() => import('./pages/TacShopCheckoutPage'));
const TacShopOrderPage = lazy(() => import('./pages/TacShopOrderPage'));
const TactiCoreTexasDownloadPage = lazy(() => import('./pages/TactiCoreTexasDownloadPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const AgenciesPage = lazy(() => import('./pages/AgenciesPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogContributePage = lazy(() => import('./pages/BlogContributePage'));
const BlogManagementPage = lazy(() => import('./pages/BlogManagementPage'));
const BlogPostEditorPage = lazy(() => import('./pages/BlogPostEditorPage'));
const BlogPostPreviewPage = lazy(() => import('./pages/BlogPostPreviewPage'));
const BlogSeriesListPage = lazy(() => import('./pages/BlogSeriesListPage'));
const BlogSeriesDetailPage = lazy(() => import('./pages/BlogSeriesDetailPage'));
const BlogAuthorPage = lazy(() => import('./pages/BlogAuthorPage'));
const BlogPostDetailPage = lazy(() => import('./pages/BlogPostDetailPage'));
const KnowledgeBaseHomePage = safeLazy(() => import('./pages/KnowledgeBaseHomePage'), 'KnowledgeBaseHomePage');
const KnowledgeBaseQuestionsPage = safeLazy(() => import('./pages/KnowledgeBaseQuestionsPage'), 'KnowledgeBaseQuestionsPage');
const KnowledgeBaseQuestionDetailPage = safeLazy(() => import('./pages/KnowledgeBaseQuestionDetailPage'), 'KnowledgeBaseQuestionDetailPage');
const KnowledgeBaseQuestionConfirmPage = lazy(() => import('./pages/KnowledgeBaseQuestionConfirmPage'));
const AuthorsPage = lazy(() => import('./pages/AuthorsPage'));
const VenuesPage = lazy(() => import('./pages/VenuesPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const SponsorsPage = lazy(() => import('./pages/SponsorsPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const VendorDetailPage = lazy(() => import('./pages/VendorDetailPage'));
const VendorProductDetailPage = lazy(() => import('./pages/VendorProductDetailPage'));
const VendorApplyPage = lazy(() => import('./pages/VendorApplyPage'));
const VendorApplySuccessPage = lazy(() => import('./pages/VendorApplySuccessPage'));
const VendorJoinPage = lazy(() => import('./pages/VendorJoinPage'));
const VendorConsolePage = lazy(() => import('./pages/VendorConsolePage'));
const AffiliatesPage = lazy(() => import('./pages/AffiliatesPage'));
const TrainingProviderServicesPage = lazy(() => import('./pages/TrainingProviderServicesPage'));
const AgencyTrainingReadinessPage = lazy(() => import('./pages/scorecard/AgencyTrainingReadinessPage'));
const TcolePsrLandingPage = lazy(() => import('./pages/tools/TcolePsrLandingPage'));
const AgencyTrainingReadinessResultsPage = lazy(() => import('./pages/scorecard/AgencyTrainingReadinessResultsPage'));
const ListTrainingInStatePage = lazy(() => import('./pages/training-provider/ListTrainingInStatePage'));
const ProviderSignupPage = lazy(() => import('./pages/ProviderSignupPage'));
const ProviderProfileCompletePage = lazy(() => import('./pages/ProviderProfileCompletePage'));
const OutreachCorrectPage = lazy(() => import('./pages/OutreachCorrectPage'));
const EmployerSignupPage = lazy(() => import('./pages/EmployerSignupPage'));
const ProfessionalSignupPage = lazy(() => import('./pages/ProfessionalSignupPage'));
const ProfessionalsDirectoryPage = lazy(() => import('./pages/ProfessionalsDirectoryPage'));
const PeopleDirectoryPage = lazy(() => import('./pages/PeopleDirectoryPage'));
const PersonProfilePage = lazy(() => import('./pages/PersonProfilePage'));
const PeopleUnlistConfirmPage = lazy(() => import('./pages/PeopleUnlistConfirmPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const CoursesPage = lazy(() => import('./pages/CoursesPage'));
const CourseCatalogDetailPage = lazy(() => import('./pages/CourseCatalogDetailPage'));
const CourseCatalogEventsArchivePage = lazy(() => import('./pages/CourseCatalogEventsArchivePage'));
const CourseCatalogRequirementsHubPage = lazy(() => import('./pages/CourseCatalogRequirementsHubPage'));
const CourseCatalogLessonPlansHubPage = lazy(() => import('./pages/CourseCatalogLessonPlansHubPage'));
const CourseCatalogComparePage = lazy(() => import('./pages/CourseCatalogComparePage'));
const RequirementsDirectoryPage = lazy(() => import('./pages/RequirementsDirectoryPage'));
const CourseTciRedirectPage = lazy(() => import('./pages/CourseTciRedirectPage'));
const TrainingRequirementDetailPage = lazy(() => import('./pages/TrainingRequirementDetailPage'));
const CredentialCatalogPage = lazy(() => import('./pages/CredentialCatalogPage'));
const CredentialDetailPage = lazy(() => import('./pages/CredentialDetailPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const CertificationPage = lazy(() => import('./pages/CertificationPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const VerifyLocationPage = lazy(() => import('./pages/VerifyLocationPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const VerifyCertificatePage = lazy(() => import('./pages/VerifyCertificatePage'));
const SecuritySettingsPage = lazy(() => import('./pages/SecuritySettingsPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const SignupChoosePage = lazy(() => import('./pages/SignupChoosePage'));
const AgencyProfilePage = lazy(() => import('./pages/AgencyProfilePage'));
const AgencyOrgChartPage = lazy(() => import('./pages/AgencyOrgChartPage'));
const AgencyOrgChartLegacyRedirect = lazy(() => import('./pages/AgencyOrgChartLegacyRedirect'));
const CoordinatorEventCreatePage = lazy(() => import('./pages/CoordinatorEventCreatePage'));
const CoordinatorConferenceCreatePage = lazy(() => import('./pages/CoordinatorConferenceCreatePage'));
const AptStudioDashboardPage = lazy(() => import('./pages/AptStudioDashboardPage'));
const AptStudioBillingPage = lazy(() => import('./pages/AptStudioBillingPage'));
const AptStudioDomainPage = lazy(() => import('./pages/AptStudioDomainPage'));
const AptStudioWorkspaceLayout = lazy(() => import('./pages/studio/AptStudioWorkspaceLayout'));
const AptStudioShopPage = lazy(() => import('./pages/studio/AptStudioShopPage'));
const AptStudioBlogPage = lazy(() => import('./pages/studio/AptStudioBlogPage'));
const AptStudioOrgSettingsPage = lazy(() => import('./pages/studio/AptStudioOrgSettingsPage'));
const AptStudioAcceptInvitePage = lazy(() => import('./pages/studio/AptStudioAcceptInvitePage'));
const AptStudioPagesListPage = lazy(() => import('./pages/studio/AptStudioPagesListPage'));
const AptStudioBrandPage = lazy(() => import('./pages/studio/AptStudioBrandPage'));
const AptStudioWhiteLabelPage = lazy(() => import('./pages/studio/AptStudioWhiteLabelPage'));
const AptStudioPageEditorPage = lazy(() => import('./pages/studio/AptStudioPageEditorRouter'));
const AptStudioEditRedirect = lazy(() => import('./pages/studio/AptStudioEditRedirect'));
const StudioBuilderRoute = lazy(() => import('./studio/builder/StudioBuilderRoute'));
const StudioPublicRouter = lazy(() => import('./studio/StudioPublicRouter'));
const AmeriConDashboardPage = lazy(() => import('./pages/americon/AmeriConDashboardPage'));
const AmeriConEditionListPage = lazy(() => import('./pages/americon/AmeriConEditionListPage'));
const AmeriConEditionSitePage = lazy(() => import('./pages/americon/AmeriConEditionSitePage'));
const AmeriConEditionContentPage = lazy(() => import('./pages/americon/AmeriConEditionContentPage'));
const AmeriConDomainPage = lazy(() => import('./pages/americon/AmeriConDomainPage'));
const AmeriConOrgSettingsPage = lazy(() => import('./pages/americon/AmeriConOrgSettingsPage'));
const AmeriConBillingPage = lazy(() => import('./pages/americon/AmeriConBillingPage'));
const AmeriConAcceptInvitePage = lazy(() => import('./pages/americon/AmeriConAcceptInvitePage'));
const AmeriConPublicRouter = lazy(() => import('./americon/AmeriConPublicRouter'));
const CoordinatorConferenceManagePage = lazy(() => import('./pages/CoordinatorConferenceManagePage'));
const ConferenceRegistrationManagePage = lazy(() => import('./pages/ConferenceRegistrationManagePage'));
const ConferenceCheckInPage = lazy(() => import('./pages/ConferenceCheckInPage'));
const ConferenceCertificateVerifyPage = lazy(() => import('./pages/ConferenceCertificateVerifyPage'));
const ConferencesHubPage = lazy(() => import('./pages/ConferencesHubPage'));
const ConferenceSpeakerInvitePage = lazy(() => import('./pages/ConferenceSpeakerInvitePage'));
const CoordinatorEventManagePage = lazy(() => import('./pages/CoordinatorEventManagePage'));
const EventCreateRedirect = lazy(() => import('./components/events/EventCreateRedirect'));
const SetupPasswordPage = lazy(() => import('./pages/SetupPasswordPage'));
const InstructorBioPage = lazy(() => import('./pages/InstructorBioPage'));
const InstructorsPage = lazy(() => import('./pages/InstructorsPage'));
const InstructorBioManagement = lazy(() => import('./components/dashboard/InstructorBioManagement'));
const InstructorBioApproval = lazy(() => import('./components/admin/InstructorBioApproval'));
const AgencyDetailPage = lazy(() => import('./pages/AgencyDetailPage'));
const UserFavoritesPage = lazy(() => import('./pages/UserFavoritesPage'));
const InstructorEventWrapper = lazy(() => import('./components/events/InstructorEventWrapper'));
const StudentEventWrapper = lazy(() => import('./components/events/StudentEventWrapper'));
const AuthTestComponent = lazy(() => import('./components/admin/AuthTestComponent'));
const CalendarFullScreenPage = lazy(() => import('./pages/CalendarFullScreenPage'));
const EmbedCalendarPage = lazy(() => import('./pages/EmbedCalendarPage'));
const LegacyDetailPathRedirect = lazy(() => import('./pages/LegacyDetailPathRedirect'));
const SingularEntityRedirect = lazy(() => import('./pages/SingularEntityRedirect'));
const FAQDetailPage = lazy(() => import('./pages/FAQDetailPage'));
// Agency detail: apex `/agencies/:slug` (legacy `agency.*` host may still hit this app via redirect)
const InstructorSlugPage = lazy(() => import('./pages/InstructorSlugPage'));
const VenueSlugPage = lazy(() => import('./pages/VenueSlugPage'));
const EventSlugPage = lazy(() => import('./pages/EventSlugPage'));
const EventSlugRouter = lazy(() => import('./components/EventSlugRouter'));
const SupervisorMembersPage = lazy(() => import('./pages/supervisor/SupervisorMembersPage'));
const SupervisorMemberDetailPage = lazy(() => import('./pages/supervisor/SupervisorMemberDetailPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const GalleryUploadPage = lazy(() => import('./pages/GalleryUploadPage'));
const K9OpsPage = lazy(() => import('./pages/K9OpsPage'));
const AptStudioPage = lazy(() => import('./pages/AptStudioPage'));
const ChatAptPage = lazy(() => import('./pages/ChatAptPage'));
const AmeriConPage = lazy(() => import('./pages/AmeriConPage'));
const MyAptPage = lazy(() => import('./pages/MyAptPage'));
const TeachAptPage = lazy(() => import('./pages/TeachAptPage'));
const K9OpsQuickMenuPage = lazy(() => import('./pages/K9OpsQuickMenuPage'));
const K9OpsDashboardPage = lazy(() => import('./pages/K9OpsDashboardPage'));
const K9TeamManagementPage = lazy(() => import('./pages/K9TeamManagementPage'));
const BackofficeChatPage = lazy(() => import('./pages/BackofficeChatPage'));
const K9ExpensePage = lazy(() => import('../k9/pages/K9ExpensePage'));
const K9TrackingPage = lazy(() => import('../k9/pages/K9TrackingPage'));
const K9TrackingSummaryPage = lazy(() => import('../k9/pages/K9TrackingSummaryPage'));
const K9DetectionPage = safeLazy(() => import('../k9/pages/K9DetectionPage'), 'K9DetectionPage');
const K9TrainingPage = lazy(() => import('../k9/pages/K9TrainingPage'));
const K9TrainingBrowsePage = lazy(() => import('../k9/pages/K9TrainingBrowsePage'));
const K9TrainingSetupTrackPage = lazy(() => import('../k9/pages/K9TrainingSetupTrackPage'));
const K9VetPage = lazy(() => import('../k9/pages/K9VetPage'));
const K9CustodyPage = lazy(() => import('../k9/pages/K9CustodyPage'));
const K9ActivityLogPage = lazy(() => import('../k9/pages/K9ActivityLogPage'));
const K9DiagnosticsPage = lazy(() => import('../k9/pages/K9DiagnosticsPage'));
const GalleryPhotoDetailPage = lazy(() => import('./pages/GalleryPhotoDetailPage'));
const GalleryPhotoEditPage = lazy(() => import('./pages/GalleryPhotoEditPage'));
const WeSaluteYouPage = lazy(() => import('./pages/WeSaluteYouPage'));
const WeSaluteYouDetailPage = lazy(() => import('./pages/WeSaluteYouDetailPage'));
const WeSaluteYouSubmissionForm = lazy(() => import('./components/we-salute-you/WeSaluteYouSubmissionForm'));
const TravelAccommodationsPage = lazy(() => import('./pages/TravelAccommodationsPage'));
const PerDiemCalculatorPage = lazy(() => import('./pages/PerDiemCalculatorPage'));
const EventRosterPage = lazy(() => import('./pages/EventRosterPage'));
const InstructorEventPage = lazy(() => import('./pages/InstructorEventPage'));
const StudentEventPage = lazy(() => import('./pages/StudentEventPage'));
const RosterManagementPage = lazy(() => import('./pages/RosterManagementPage'));
const RefundApprovalsPage = lazy(() => import('./pages/RefundApprovalsPage'));
const TactiCoreRootRouter = lazy(() => import('./components/tacticore/TactiCoreRootRouter'));
const TacticoreHostShell = lazy(() => import('./components/tacticore/TacticoreHostShell'));
const LessonPlansManager = lazy(() => import('./components/tacticore/LessonPlansManager'));
const LessonPlanLibraryManager = lazy(() => import('./components/tacticore/LessonPlanLibraryManager'));
const LessonPlanFormPage = lazy(() => import('./pages/LessonPlanFormPage'));
const LessonPlanViewPage = lazy(() => import('./pages/LessonPlanViewPage'));
const ExamsManager = lazy(() => import('./components/tacticore/ExamsManager'));
const DocumentsLibraryManager = lazy(() => import('./components/tacticore/DocumentsLibraryManager'));
const ReferencesLibraryManager = lazy(() => import('./components/tacticore/ReferencesLibraryManager'));
const LicenseManagementPage = lazy(() => import('./pages/LicenseManagementPage'));
const TactiCoreActivityLogsManager = lazy(() => import('./components/tacticore/TactiCoreActivityLogsManager'));
const ExamFormPage = lazy(() => import('./pages/ExamFormPage'));
const InstructorBiosContent = lazy(() => import('./pages/backoffice/components/InstructorBiosContent'));
const TXRampCompliancePage = lazy(() => import('./pages/TXRampCompliancePage'));
const UpcomingFeaturesPage = lazy(() => import('./pages/UpcomingFeaturesPage'));
const RestaurantsPage = lazy(() => import('./pages/RestaurantsPage'));
const RestaurantDetailPage = lazy(() => import('./pages/RestaurantDetailPage'));
const RestaurantSearch = lazy(() => import('./components/restaurants/RestaurantSearch'));

// Lazy load heavy components
const UserPreferencesPanel = lazy(() => import('./components/personalization/UserPreferencesPanel'));
const EnhancedAnalytics = lazy(() => import('./components/analytics/EnhancedAnalytics'));
const SignupSuccessPage = lazy(() => import('./pages/SignupSuccessPage'));

// Lazy load additional components that were missing
const TrainingManagerEventManagementPage = lazy(() => import('./pages/TrainingManagerEventManagementPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));

/** Matches Layout / HomePage shell reserve so footer never parks in the Lighthouse viewport. */
const HomeRouteFallback = () => (
  <Box
    aria-hidden
    sx={{
      minHeight: PUBLIC_SHELL_MIN_HEIGHT,
      width: '100%',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    }}
  />
);

/** Neutral tall fallback for public course (and similar) Suspense boundaries. */
const PublicShellFallback = () => (
  <Box
    aria-hidden
    sx={{
      minHeight: PUBLIC_SHELL_MIN_HEIGHT,
      width: '100%',
      bgcolor: 'background.default',
    }}
  />
);

// Loading component for Suspense
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

// Embed iframe: ensure loader is visible when lazy chunk loads (min height for preview area)
const EmbedRouteLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, backgroundColor: '#f5f5f5' }}>
    <CircularProgress />
  </Box>
);
const RestaurantRecommendations = lazy(() => import('./components/restaurants/RestaurantRecommendations'));
const RestaurantSuggestionForm = lazy(() => import('./components/restaurants/RestaurantSuggestionForm'));
const RestaurantFeaturesPage = lazy(() => import('./pages/RestaurantFeaturesPage'));
const LEODiningPage = lazy(() => import('./pages/LEODiningPage'));
const DataExplorerPage = lazy(() => import('./pages/admin/DataExplorerPage'));
const SystemSettingsPage = lazy(() => import('./pages/backoffice/SystemSettingsPage'));
import AntiDownloadProvider from './components/security/AntiDownloadProvider';
// Reports pages
const EventReportsPage = lazy(() => import('./pages/reports/EventReportsPage'));
const PerformanceReportsPage = lazy(() => import('./pages/reports/PerformanceReportsPage'));
const SignInSheetsPage = lazy(() => import('./pages/reports/SignInSheetsPage'));

// PWA Pages
const MePWAPage = lazy(() => import('./pages/MePWAPage'));
const InstructorPWAPage = lazy(() => import('./pages/InstructorPWAPage'));

// Blog Subdomain Router Component
const BlogSubdomainRouter: React.FC = () => {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        flex: 1,
        minHeight: 0,
        backgroundColor: 'background.default'
      }}
    >
      <Header />
      <ProviderAccessBanner />
      <Box 
        component="main" 
        role="main"
        aria-label="Main content"
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          py: 3,
          overflow: 'visible',
          minHeight: 0,
          position: 'relative'
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible', position: 'relative', px: AGENCY_SHELL_HORIZONTAL_PX }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<BlogPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/contribute" element={<ProtectedRoute><BlogContributePage /></ProtectedRoute>} />
              <Route path="/blog/contribute" element={<ProtectedRoute><BlogContributePage /></ProtectedRoute>} />
              <Route path="/manage" element={<ProtectedRoute><BlogManagementPage /></ProtectedRoute>} />
              <Route path="/blog/manage" element={<ProtectedRoute><BlogManagementPage /></ProtectedRoute>} />
              <Route path="/manage/new" element={<ProtectedRoute><BlogPostEditorPage /></ProtectedRoute>} />
              <Route path="/blog/manage/new" element={<ProtectedRoute><BlogPostEditorPage /></ProtectedRoute>} />
              <Route path="/manage/:id/edit" element={<ProtectedRoute><BlogPostEditorPage /></ProtectedRoute>} />
              <Route path="/blog/manage/:id/edit" element={<ProtectedRoute><BlogPostEditorPage /></ProtectedRoute>} />
              <Route path="/manage/:id/preview" element={<ProtectedRoute><BlogPostPreviewPage /></ProtectedRoute>} />
              <Route path="/blog/manage/:id/preview" element={<ProtectedRoute><BlogPostPreviewPage /></ProtectedRoute>} />
              <Route path="/series" element={<BlogSeriesListPage />} />
              <Route path="/blog/series" element={<BlogSeriesListPage />} />
              <Route path="/series/:slug" element={<BlogSeriesDetailPage />} />
              <Route path="/blog/series/:slug" element={<BlogSeriesDetailPage />} />
              <Route path="*" element={<BlogPostDetailPage />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

// Author Subdomain Router Component
const AuthorSubdomainRouter: React.FC = () => {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        flex: 1,
        minHeight: 0,
        backgroundColor: 'background.default'
      }}
    >
      <Header />
      <ProviderAccessBanner />
      <Box 
        component="main" 
        role="main"
        aria-label="Main content"
        sx={{ 
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          py: 3
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/:slug" element={<BlogAuthorPage />} />
              <Route path="/" element={<AuthorsPage />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

// NotFoundDetector Component - Detects 404 redirects from nginx and shows modal
const NotFoundDetector: React.FC = () => {
  const location = useLocation();
  const [show404Modal, setShow404Modal] = useState(false);
  const [attemptedUrl, setAttemptedUrl] = useState('');

  useEffect(() => {
    // Check for 404 query params on mount and location changes
    const searchParams = new URLSearchParams(location.search);
    const is404 = searchParams.get('404') === 'true';
    const url = searchParams.get('url');

    if (is404 && url) {
      setAttemptedUrl(decodeURIComponent(url));
      setShow404Modal(true);
    }
  }, [location]);

  const handleClose404Modal = () => {
    setShow404Modal(false);
    // Clean up URL query params using replaceState
    const cleanUrl = window.location.pathname;
    window.history.replaceState(null, '', cleanUrl);
  };

  return (
    <NotFoundModal
      isOpen={show404Modal}
      attemptedUrl={attemptedUrl}
      onClose={handleClose404Modal}
    />
  );
};

// Renders SEOHead for 404 pages when URL has ?404=true&url=... - must render after route content so its meta overrides.
const NotFoundSEOOverride: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const is404 = searchParams.get('404') === 'true' && searchParams.get('url');
  if (!is404) return null;
  return (
    <SEOHead
      title="Page Not Found - American Police Training"
      description="Page not found - The requested page does not exist. Browse American Police Training events, courses, and agencies."
    />
  );
};

/** Apex (www) K9 ops pages: same chrome as quick-menu / expense so PWA tiles are not blank. */
const K9OpsApexStandaloneShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <K9ChromeProvider hasGlobalSiteHeader>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <Header />
      <ProviderAccessBanner />
      <Box
        component="main"
        role="main"
        aria-label="Main content"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          py: 3,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
          <Suspense fallback={<PageLoader />}>{children}</Suspense>
        </Box>
      </Box>
      <Footer />
    </Box>
  </K9ChromeProvider>
);

/**
 * Which white-label public router owns this page load, if any.
 *
 * Resolved once at module load rather than from `useLocation`, for two reasons: the tenant
 * routers read `?siteId=` once on mount, and in-tenant navigation drops the query, so a
 * per-render check would flip the whole app back to the platform shell mid-session.
 *
 * A tenant hostname claims the page outright. A local dev host only ever renders Studio, and
 * only with an explicit `?siteId=`: that query is Studio's tenant selector, AmeriCon resolves
 * its org from the hostname alone, and ordinary `localhost` work needs the platform. That query
 * path is how template preview screenshots are captured — see
 * `scripts/studio/capture-template-previews.ts`.
 */
const PUBLIC_TENANT_ROUTER: 'americon' | 'studio' | null = (() => {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const search = new URLSearchParams(window.location.search);
  const studioSiteRequested = Boolean(search.get('siteId') || search.get('site_id'));
  const devPreviewEnabled =
    import.meta.env.DEV === true
    || import.meta.env.VITE_APT_STUDIO_DEV_PREVIEW === 'true'
    || import.meta.env.VITE_AMERICON_DEV_PREVIEW === 'true';
  const viteStudioEnabled = import.meta.env.VITE_APT_STUDIO_ENABLED === 'true';

  if (isLocalDevHostname(hostname)) {
    const studioPreview =
      studioSiteRequested
      && shouldMountStudioPublicRouter({ hostname, viteStudioEnabled, devPreviewEnabled });
    return studioPreview ? 'studio' : null;
  }

  if (shouldMountAmeriConPublicRouter({
    hostname,
    viteAmeriConEnabled: import.meta.env.VITE_AMERICON_ENABLED === 'true',
    devPreviewEnabled,
  })) {
    return 'americon';
  }
  if (shouldMountStudioPublicRouter({ hostname, viteStudioEnabled, devPreviewEnabled })) {
    return 'studio';
  }
  return null;
})();

/** Tenant public router for this page load, or `null` to fall through to platform routing. */
const PublicTenantRouterOutlet: React.FC = () => {
  if (PUBLIC_TENANT_ROUTER === 'americon') {
    return (
      <Suspense fallback={<PageLoader />}>
        <AmeriConPublicRouter />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <StudioPublicRouter />
    </Suspense>
  );
};

// Subdomain-aware content: must live inside Router so useLocation triggers re-render on navigation.
// Without this, in-app path changes (e.g. agency detail) would update the URL but App wouldn't
// re-render (it's the Router's parent), so nested routes would not refresh correctly.
const SubdomainAwareContent: React.FC = () => {
  useLocation(); // Subscribe to location changes so this component re-renders when pathname changes
  const currentSubdomainInfo = detectSubdomain(); // Re-read on every render (including after nav)
  return <SubdomainRouter subdomainInfo={currentSubdomainInfo} />;
};

/** On backoffice.<domain> the `/backoffice` prefix is redundant; keep one canonical URL shape. */
const BackofficePrefixRedirect: React.FC = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`${stripBackofficePrefix(location.pathname)}${location.search}${location.hash}`}
      replace
    />
  );
};

// Subdomain Router Component
const SubdomainRouter: React.FC<{ subdomainInfo: any }> = ({ subdomainInfo }) => {

  switch (subdomainInfo.subdomain) {
    case SUBDOMAINS.K9:
      return (
        <ErrorBoundary fallback={(error) => <K9ErrorFallback error={error ?? null} />}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/*
                k9.* is the PWA app host: keep the visible URL under /k9-ops/* so the service worker
                scope matches k9-ops-manifest.json start_url and K9OpsPwaManager (pathname /k9-ops/…).
                Marketing lives on apex /k9-ops (see K9OpsPage).
              */}
              <Route path="/" element={<K9SubdomainEntryRedirect />} />
              <Route path="/k9-ops/quick-menu" element={<K9OpsQuickMenuPage />} />
              <Route path="/k9-ops/dashboard" element={<K9OpsDashboardPage />} />
              <Route path="/k9-ops/expense" element={<K9ExpensePage />} />
              <Route path="/k9-ops/tracking" element={<K9TrackingPage />} />
              <Route path="/k9-ops/tracking/:sessionId/summary" element={<K9TrackingSummaryPage />} />
              <Route path="/k9-ops/detection" element={<K9DetectionPage />} />
              <Route path="/k9-ops/training" element={<K9TrainingPage />} />
              <Route path="/k9-ops/training/browse" element={<K9TrainingBrowsePage />} />
              <Route path="/k9-ops/training/setup/track" element={<K9TrainingSetupTrackPage />} />
              <Route path="/k9-ops/vet" element={<K9VetPage />} />
              <Route path="/k9-ops/custody" element={<K9CustodyPage />} />
              <Route path="/k9-ops/activity" element={<K9ActivityLogPage />} />
              <Route path="/k9-ops/diagnostics" element={<K9DiagnosticsPage />} />
              <Route path="/k9-ops/team" element={<K9TeamManagementPage />} />
              <Route path="/quick-menu" element={<Navigate to="/k9-ops/quick-menu" replace />} />
              <Route path="/dashboard" element={<Navigate to="/k9-ops/dashboard" replace />} />
              <Route path="*" element={<K9SubdomainEntryRedirect />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      );
    
    case SUBDOMAINS.ME:
      return (
        <Suspense fallback={<PageLoader />}>
          <MeRoutes />
        </Suspense>
      );
    
    case SUBDOMAINS.TEACH:
      return (
        <Suspense fallback={<PageLoader />}>
          <TeachRoutes />
        </Suspense>
      );

    case SUBDOMAINS.CHAT:
      return (
        <Suspense fallback={<PageLoader />}>
          <ChatRoutes />
        </Suspense>
      );
    
    case SUBDOMAINS.BACKOFFICE:
      return (
        <Routes>
          {/* Logout confirmation - no auth required */}
          <Route path="/logout-confirmation" element={
                        <Suspense fallback={<PageLoader />}>
                          <LogoutConfirmationPage />
                        </Suspense>
                      } />
          {/* backoffice.<domain>/backoffice/x is redundant — canonicalize to /x */}
          <Route path="/backoffice/*" element={<BackofficePrefixRedirect />} />
          <Route path="/backoffice" element={<BackofficePrefixRedirect />} />
          {/* All other backoffice routes require authentication */}
          <Route path="/*" element={
            <ProtectedRoute requireBackofficeRole={true}>
              <Suspense fallback={<PageLoader />}>
                <BackofficePage />
              </Suspense>
            </ProtectedRoute>
          } />
        </Routes>
      );
    
    case SUBDOMAINS.AGENCIES:
      // AGENCIES subdomain case
      // If there's an agency slug, show the agency detail page
      if (subdomainInfo.agencySlug) {
        // Rendering AgencyDetailPage for slug
        return (
          <PlatformPageWrapper>
            <Box sx={listedPublicSubdomainOuterSx}>
              <Header />
              <ProviderAccessBanner />
              <Box
                component="main"
                role="main"
                aria-label="Main content"
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  py: 3,
                }}
              >
                <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                  <ErrorBoundary
                    fallback={(error) => (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6" color="error">
                          Unable to load agency details
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          The agency you are looking for could not be loaded.
                        </Typography>
                        {import.meta.env.DEV && error && (
                          <Alert severity="info" sx={{ mt: 2, textAlign: 'left', maxWidth: 600 }}>
                            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {error.message}
                            </Typography>
                          </Alert>
                        )}
                        <Button
                          href={getNavigationUrl('/agencies')}
                          variant="contained"
                          sx={{ mt: 2 }}
                          startIcon={<ArrowBack />}
                        >
                          Back to Agencies
                        </Button>
                      </Box>
                    )}
                  >
                    <Suspense fallback={<PublicShellFallback />}>
                      <AgencyDetailPage />
                    </Suspense>
                  </ErrorBoundary>
                </Box>
              </Box>
              <Footer />
            </Box>
          </PlatformPageWrapper>
        );
      }
      // Otherwise, show the agencies listing page
      // Rendering AgenciesPage (no slug)
      return (
        <PlatformPageWrapper>
          <Box sx={listedPublicSubdomainOuterSx}>
            <Header />
            <ProviderAccessBanner />
            <Box
              component="main"
              role="main"
              aria-label="Main content"
              sx={{
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3,
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PublicShellFallback />}>
                  <AgenciesPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.BLOG:
      return <BlogSubdomainRouter />;
    
    case SUBDOMAINS.AUTHOR:
      return (
        <PlatformPageWrapper>
          <AuthorSubdomainRouter />
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.SHOP:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PublicShellFallback />}>
                  <TacShopPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.TRAINING:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PageLoader />}>
                  <CoursesPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.GALLERY:
      return (
        <PlatformPageWrapper>
          <Box sx={listedPublicSubdomainOuterSx}>
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Routes>
                  <Route path="/" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <GalleryPage />
                    </Suspense>
                  } />
                  <Route path="upload" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <GalleryUploadPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="photo/:id" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <GalleryPhotoDetailPage />
                    </Suspense>
                  } />
                  <Route path="edit/:id" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <GalleryPhotoEditPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                </Routes>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.DOCS:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PublicShellFallback />}>
                  <DocsPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.SUPPORT:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PublicShellFallback />}>
                  <SupportPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.FAQ:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Routes>
                  <Route path="/" element={
                    <Suspense fallback={<PageLoader />}>
                      <FAQPage />
                    </Suspense>
                  } />
                  <Route
                    path="/:slug"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <FAQDetailPage />
                      </Suspense>
                    }
                  />
                </Routes>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.DASH:
      return (
        <Routes>
          {/* Embed calendar - no dashboard layout so preview iframe shows only the calendar. */}
          <Route
            path="/embed/calendar/:agencyId"
            element={
              <Suspense fallback={<PageLoader />}>
                <EmbedCalendarPage />
              </Suspense>
            }
          />
          <Route path="*" element={
        <Box
          sx={{
            height: '100vh',
            maxHeight: '100vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        <DashPWAManager>
          <PlatformPageWrapper>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                backgroundColor: 'background.default'
              }}
            >
              <Header />
              <ProviderAccessBanner />
              <Box 
                component="main" 
                role="main"
                aria-label="Main content"
                sx={{ 
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  py: 3,
                  position: 'relative'
                }}
              >
                <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible', position: 'relative', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                  <Routes>
                    <Route path="/logout-confirmation" element={
                        <Suspense fallback={<PageLoader />}>
                          <LogoutConfirmationPage />
                        </Suspense>
                      } />
                    {/* Dashboard routes (legacy `dash.*` host; mirror paths on apex `/dashboard`) */}
                    <Route path="/" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <DashboardPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <DashboardPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                  <Route path="/dashboard/instructor-bio/:instructorId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <InstructorBioManagement />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/instructor-bio" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <InstructorBioManagement />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/profile" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ProfilePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route
                    path="/dashboard/people-profile"
                    element={<Navigate to="/dashboard/settings?tab=profile" replace />}
                  />
                  <Route path="/dashboard/settings" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <DashboardSettingsPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/coordinator/office-submissions" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <AgencyOfficeSubmissionsPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/coordinator/training-plan" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <AgencyTrainingPlanPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/settings/security" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <SecuritySettingsPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/agency-profile" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <AgencyProfilePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/agencies/org-chart" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <AgencyOrgChartPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/agencies/:agencyId/org-chart" element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyOrgChartLegacyRedirect />
                    </Suspense>
                  } />
                  <Route path="/agency/org-chart" element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyOrgChartLegacyRedirect />
                    </Suspense>
                  } />
                  <Route path="/dashboard/event-management" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <TrainingManagerEventManagementPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/coordinator-event-create" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/training-manager-event-create" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/coordinator-event-create" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/coordinator-conference-create" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorConferenceCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/coordinator-conference-edit/:eventId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorConferenceCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/coordinator-conference-manage/:eventId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorConferenceManagePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/events/:slug/registration/:token" element={
                    <Suspense fallback={<PageLoader />}>
                      <ConferenceRegistrationManagePage />
                    </Suspense>
                  } />
                  <Route path="/events/:slug/check-in/:token" element={
                    <Suspense fallback={<PageLoader />}>
                      <ConferenceCheckInPage />
                    </Suspense>
                  } />
                  <Route path="/conferences/certificates/:code" element={
                    <Suspense fallback={<PageLoader />}>
                      <ConferenceCertificateVerifyPage />
                    </Suspense>
                  } />
                  <Route path="/dashboard/coordinator-conference-create" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorConferenceCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/studio" element={
                    <AptStudioLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AptStudioWorkspaceLayout />
                      </Suspense>
                    </AptStudioLaunchProtected>
                  }>
                    <Route index element={<AptStudioDashboardPage />} />
                    <Route path="pages" element={<AptStudioPagesListPage />} />
                    <Route path="brand" element={<AptStudioBrandPage />} />
                    <Route path="shop" element={<AptStudioShopPage />} />
                    <Route path="blog" element={<AptStudioBlogPage />} />
                    <Route path="domains" element={<AptStudioDomainPage />} />
                    <Route path="billing" element={<AptStudioBillingPage />} />
                    <Route path="white-label" element={<AptStudioWhiteLabelPage />} />
                    <Route path="settings" element={<AptStudioOrgSettingsPage />} />
                  </Route>
                  <Route path="/dashboard/studio/accept-invite" element={
                    <StudioAmericonDashboardLaunchGate product="apt_studio">
                      <Suspense fallback={<PageLoader />}>
                        <AptStudioAcceptInvitePage />
                      </Suspense>
                    </StudioAmericonDashboardLaunchGate>
                  } />
                  <Route path="/dashboard/studio/edit" element={
                    <AptStudioLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AptStudioEditRedirect />
                      </Suspense>
                    </AptStudioLaunchProtected>
                  } />
                  <Route path="/dashboard/studio/pages/:pageId/edit" element={
                    <AptStudioLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AptStudioPageEditorPage />
                      </Suspense>
                    </AptStudioLaunchProtected>
                  } />
                  <Route path="/dashboard/studio/builder/:pageId" element={
                    <AptStudioLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <StudioBuilderRoute />
                      </Suspense>
                    </AptStudioLaunchProtected>
                  } />
                  <Route path="/dashboard/americon" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConDashboardPage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/orgs/:orgId/editions" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConEditionListPage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/orgs/:orgId/editions/:editionId/site" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConEditionSitePage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/orgs/:orgId/editions/:editionId/content" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConEditionContentPage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/orgs/:orgId/domains" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConDomainPage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/orgs/:orgId/settings" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConOrgSettingsPage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/orgs/:orgId/billing" element={
                    <AmeriConLaunchProtected>
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConBillingPage />
                      </Suspense>
                    </AmeriConLaunchProtected>
                  } />
                  <Route path="/dashboard/americon/accept-invite" element={
                    <StudioAmericonDashboardLaunchGate product="americon">
                      <Suspense fallback={<PageLoader />}>
                        <AmeriConAcceptInvitePage />
                      </Suspense>
                    </StudioAmericonDashboardLaunchGate>
                  } />
                  <Route path="/coordinator-event-edit/:eventId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  {/* Event edit/manage by numeric ID (e.g. from Training Manager or links) */}
                  <Route path="/events/:eventId/edit" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/events/:eventId/roster" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <EventRosterPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/coordinator-event-manage/:eventId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventManagePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/training-manager-event-create" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/instructor/event/:eventId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <InstructorEventPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/instructor/refund-approvals" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <RefundApprovalsPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/student/event/:eventId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <StudentEventPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  {/* Premium subscription routes */}
                  <Route path="/premium" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <PremiumPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/subscription/settings" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <SubscriptionSettingsPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/subscription/success" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <SubscriptionSuccessPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-success" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <PaymentSuccessPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  {/* Email verification (public - from welcome email link) */}
                  <Route path="/verify-email/:token" element={
                    <Suspense fallback={<PageLoader />}>
                      <VerifyEmailPage />
                    </Suspense>
                  } />
                  {/* Settings/Profile route */}
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ProfilePage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  {/* Change password route - wrapped in ProtectedRoute to ensure session is valid */}
                  <Route path="/change-password" element={
                    <ProtectedRoute allowPasswordChange>
                      <Suspense fallback={<PageLoader />}>
                        <ChangePasswordPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  {/* TactiCore short paths → apex `/tacticore/*` */}
                  <Route path="/download" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/lesson-plans/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/exams/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/documents/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/references/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/library/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/license-management/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/instructor-bios/*" element={<RedirectShortPathToTacticoreApp />} />
                  <Route path="/activity-logs/*" element={<RedirectShortPathToTacticoreApp />} />
                  {/* Public agency directory on dash host (relative /agencies was previously caught by * → dashboard) */}
                  <Route path="/agencies/org-chart" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <AgencyOrgChartPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/agencies/:agencyId/org-chart" element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyOrgChartLegacyRedirect />
                    </Suspense>
                  } />
                  <Route path="/agency/org-chart" element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyOrgChartLegacyRedirect />
                    </Suspense>
                  } />
                  <Route path="/agencies" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <AgenciesPage />
                    </Suspense>
                  } />
                  <Route path="/agencies/:id" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <AgencyDetailPage />
                    </Suspense>
                  } />
                  <Route path="/agencies/:id/recruiting/jobs/:jobSlug" element={
                    <Suspense fallback={<PageLoader />}>
                      <AgencyRecruitingJobDetailPage />
                    </Suspense>
                  } />
                  {/* Catch-all for dashboard subdomain */}
                  <Route path="*" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <DashboardPage />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                </Routes>
              </Box>
              <Footer />
            </Box>
          </Box>
        </PlatformPageWrapper>
        </DashPWAManager>
        </Box>
          } />
        </Routes>
      );
  
  case SUBDOMAINS.CERTIFICATION:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PublicShellFallback />}>
                  <CertificationPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.TACTICORE:
      return (
        <PlatformPageWrapper>
          <Suspense fallback={<PageLoader />}>
            <TacticoreHostShell />
          </Suspense>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.VENUE:
      return (
        <PlatformPageWrapper>
          <Box sx={listedPublicSubdomainOuterSx}>
            <Header />
            <ProviderAccessBanner />
            <Box
              component="main"
              role="main"
              aria-label="Main content"
              sx={{
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3,
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Routes>
                  {/* Root route - show venues listing */}
                  <Route path="/" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <VenuesPage />
                    </Suspense>
                  } />
                  {/* Handle /venue/:slug pattern and redirect to correct format */}
                  <Route path="/venue/:venueSlug" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <VenueSlugPage />
                    </Suspense>
                  } />
                  {/* Slug-based route for individual venue pages */}
                  <Route path="/:venueSlug" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <VenueSlugPage />
                    </Suspense>
                  } />
                </Routes>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.INSTRUCTOR:
      return (
        <PlatformPageWrapper>
          <Box sx={listedPublicSubdomainOuterSx}>
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Routes>
                  {/* Root route - show instructors listing */}
                  <Route path="/" element={
                    <Suspense fallback={<PublicShellFallback />}>
                      <InstructorsPage />
                    </Suspense>
                  } />
                  {/* Slug-based route for individual instructor profiles */}
                  <Route path="/:instructorSlug" element={
                    <Suspense fallback={<PageLoader />}>
                      <InstructorBioPage />
                    </Suspense>
                  } />
                </Routes>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.EVENT:
      return (
        <PlatformPageWrapper>
          <Box sx={listedPublicSubdomainOuterSx}>
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Routes>
                  <Route path="/" element={
                    <Suspense fallback={<PageLoader />}>
                      <EventsPage />
                    </Suspense>
                  } />
                  <Route path="events/create" element={
                    <Suspense fallback={<PageLoader />}>
                      <EventCreateRedirect />
                    </Suspense>
                  } />
                  <Route path=":slug" element={
                    <Suspense fallback={<PageLoader />}>
                      <EventSlugRouter />
                    </Suspense>
                  } />
                </Routes>
              </Box>
            </Box>
            <Footer />
            <TrainingAlertsNudgeManager />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.API:
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PublicShellFallback />}>
                  <DocsPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    case SUBDOMAINS.EAT:
      // EAT subdomain - /suggest is public (form handles auth on submit); other routes require auth
      const eatLayout = (
        <Box sx={listedPublicSubdomainOuterSx}>
          <Header />
          <ProviderAccessBanner />
          <Box
            component="main"
            role="main"
            aria-label="Main content"
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              py: 3
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </Box>
          </Box>
          <Footer />
        </Box>
      );
      return (
        <Routes>
          <Route path="/suggest" element={<PlatformPageWrapper>{eatLayout}</PlatformPageWrapper>}>
            <Route index element={<RestaurantSuggestionForm />} />
          </Route>
          <Route path="/" element={<ProtectedRoute requireAuth={true}><PlatformPageWrapper>{eatLayout}</PlatformPageWrapper></ProtectedRoute>}>
            <Route index element={<RestaurantsPage />} />
            <Route path="restaurants" element={<RestaurantsPage />} />
            <Route path="restaurants/:slug" element={<RestaurantDetailPage />} />
            <Route path=":slug" element={<RestaurantDetailPage />} />
            <Route path="*" element={<RestaurantsPage />} />
          </Route>
        </Routes>
      );

    case SUBDOMAINS.TRAVEL:
      // Travel accommodations (Hotels.com) - travel.americanpolicetraining.com
      return (
        <PlatformPageWrapper>
          <Box sx={listedPublicSubdomainOuterSx}>
            <Header />
            <ProviderAccessBanner />
            <Box
              component="main"
              role="main"
              aria-label="Main content"
              sx={{
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Suspense fallback={<PageLoader />}>
                  <TravelAccommodationsPage />
                </Suspense>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
    
    default:
      // Unknown subdomain - show homepage
      return (
        <PlatformPageWrapper>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: '100vh',
              backgroundColor: 'background.default'
            }}
          >
            <Header />
            <ProviderAccessBanner />
            <Box 
              component="main" 
              role="main"
              aria-label="Main content"
              sx={{ 
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                py: 3
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                <Routes>
                  <Route path="*" element={
                    <Suspense fallback={<HomeRouteFallback />}>
                      <HomePage />
                    </Suspense>
                  } />
                </Routes>
              </Box>
            </Box>
            <Footer />
          </Box>
        </PlatformPageWrapper>
      );
  }
};

const App: React.FC = () => {
  // Initialize security monitoring
  useEffect(() => {
    if (import.meta.env.PROD) {
      securityMonitor.start();
    }
  }, []);

  useEffect(() => {
    registerGlobalClientErrorHandlers();
    registerBrowserCsrfLifecycleHandlers();
  }, []);

  // Detect current subdomain
  const subdomainInfo = detectSubdomain();
  
  // Handle eat subdomain - no redirect, just serve restaurant content
  // The subdomain routing will handle this in the SubdomainRouter component
  
  // Optimize logging to prevent performance issues
  if (import.meta.env.DEV) {
    // Use requestIdleCallback for non-critical logging
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        // App component debugging removed
        
        // Debug logging for backoffice subdomain
        if (window.location.hostname.includes('backoffice')) {
          // BACKOFFICE SUBDOMAIN DETECTED
        }
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        // App component debugging removed
        
        // Debug logging for backoffice subdomain
        if (window.location.hostname.includes('backoffice')) {
          // BACKOFFICE SUBDOMAIN DETECTED
        }
      }, 0);
    }
  }
  
  // Initialize access policies
  const { isInitialized: policiesInitialized } = useAccessPolicies({
    autoInitialize: true,
    configuration: {
      enableABAC: true,
      enableMonitoring: true,
      debug: {
        enabled: import.meta.env.DEV,
        logLevel: 'info'
      }
    }
  });

  // Prevent third-party editor extensions from mutating form fields
  useExtensionCompatibility();

  // Initialize tracking prevention handling early
  useEffect(() => {
    const initializeTracking = () => {
      try {
        if (import.meta.env.DEV) {
          // Initializing tracking prevention
        }
        initializeTrackingPrevention();
        
        // Suppress browser extension errors globally
        suppressBrowserExtensionErrorsTemporarily(30000); // 30 seconds for app initialization
        
        // Debug logging for development (only after initialization)
        if (import.meta.env.MODE === 'development') {
          // LocalStorage auth debugging removed
        }
      } catch (error) {
        logger.warn('Tracking prevention initialization failed, continuing without it', { error: error.message });
        // App can continue without tracking prevention handling
      }
    };

    // Use microtask for immediate execution with better performance
    Promise.resolve().then(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(initializeTracking, { timeout: 300 });
      } else {
        initializeTracking();
      }
    });
  }, []);

  // Switch manifest for dash subdomain PWA
  useEffect(() => {
    if (subdomainInfo.subdomain === SUBDOMAINS.DASH) {
      const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      if (manifestLink) {
        manifestLink.href = '/dash-manifest.json';
        logger.info('Dash manifest loaded');
      }
    }
  }, [subdomainInfo.subdomain]);

  // Chat subdomain: staff Support Chat PWA
  useEffect(() => {
    if (subdomainInfo.subdomain === SUBDOMAINS.CHAT) {
      const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      if (manifestLink) {
        manifestLink.href = '/chat-manifest.json';
        logger.info('Chat Support PWA manifest loaded');
      }
    }
  }, [subdomainInfo.subdomain]);

  // K9 subdomain: same manifest as APEX K9™ Quick Menu so installs from k9.* open /k9-ops/quick-menu per k9-ops-manifest.json
  useEffect(() => {
    if (subdomainInfo.subdomain === SUBDOMAINS.K9) {
      const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      if (manifestLink) {
        manifestLink.href = '/k9-ops-manifest.json';
        logger.info('K9 subdomain manifest loaded');
      }
    }
  }, [subdomainInfo.subdomain]);

  // Identical viewport meta on every surface (including K9) so initial scale matches across subdomains.
  useEffect(() => {
    applyCanonicalViewportMeta();
  }, [subdomainInfo.subdomain]);

  useDropdownBackdropSafetyNet();

  // Initialize analytics with optimized performance
  useEffect(() => {
    const initializeAnalytics = () => {
      try {
        // Track initial page view
        trackPageView();
        
        // Track app initialization
        trackEvent({
          action: 'app_initialized',
          category: 'application',
          custom_parameters: {
            user_agent: navigator.userAgent,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        });
      } catch (error) {
        logger.warn('Analytics initialization failed', { error: error.message });
      }
    };

    // Use microtask for better performance
    Promise.resolve().then(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(initializeAnalytics, { timeout: 200 });
      } else {
        initializeAnalytics();
      }
    });
  }, []);

  const [deferredThirdPartyReady, setDeferredThirdPartyReady] = useState(false);
  const [deferredAdSenseReady, setDeferredAdSenseReady] = useState(false);
  useEffect(() => {
    const markReady = () => setDeferredThirdPartyReady(true);
    let timerId: number | null = null;
    let idleId: number | null = null;

    const schedule = () => {
      if ('requestIdleCallback' in window) {
        idleId = (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number }).requestIdleCallback(markReady, { timeout: 2000 });
      } else {
        timerId = window.setTimeout(markReady, 1200);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    return () => {
      window.removeEventListener('load', schedule);
      if (idleId !== null && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback: (handle: number) => void }).cancelIdleCallback(idleId);
      }
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  // AdSense/FundingChoices after analytics: idle, first interaction, or max wait — reduces TBT/LCP contention.
  // Funding Choices cannot load before this gate: GoogleAdSenseLoader (adsbygoogle.js) mounts only when ready;
  // premium/`data-adsense-blocked` still strips FC early in index.html.
  useEffect(() => {
    if (!deferredThirdPartyReady || deferredAdSenseReady) {
      return;
    }
    let cancelled = false;
    let idleId: number | null = null;
    let maxTimerId: number | null = null;

    const markAdsReady = () => {
      if (cancelled) {
        return;
      }
      cancelled = true;
      setDeferredAdSenseReady(true);
      window.removeEventListener('pointerdown', markAdsReady);
      window.removeEventListener('keydown', markAdsReady);
      window.removeEventListener('scroll', markAdsReady);
      if (idleId !== null && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback: (handle: number) => void }).cancelIdleCallback(idleId);
      }
      if (maxTimerId !== null) {
        window.clearTimeout(maxTimerId);
      }
    };

    window.addEventListener('pointerdown', markAdsReady, { once: true, passive: true });
    window.addEventListener('keydown', markAdsReady, { once: true });
    window.addEventListener('scroll', markAdsReady, { once: true, passive: true });

    if ('requestIdleCallback' in window) {
      idleId = (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number }).requestIdleCallback(markAdsReady, { timeout: 4500 });
    }
    maxTimerId = window.setTimeout(markAdsReady, 5000);

    return () => {
      cancelled = true;
      window.removeEventListener('pointerdown', markAdsReady);
      window.removeEventListener('keydown', markAdsReady);
      window.removeEventListener('scroll', markAdsReady);
      if (idleId !== null && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback: (handle: number) => void }).cancelIdleCallback(idleId);
      }
      if (maxTimerId !== null) {
        window.clearTimeout(maxTimerId);
      }
    };
  }, [deferredThirdPartyReady, deferredAdSenseReady]);

  // Route changes are now handled by GoogleAnalytics component

  return (
    <ReactProvider>
      <ReduxProvider store={store}>
        <ErrorBoundary
          onError={(error, errorInfo) => {
            // Log securely without exposing to UI
            logger.error('Global error caught', error, {
              componentStack: errorInfo.componentStack
            });
          }}
        >
          <MuiThemeContextProvider>
            <SessionExpiredProvider>
              <SessionWarningProvider>
                <Favicon />
                <HelmetProvider>
                  <Router>
                  <ScrollToTopOnNavigate />
                  {/* 404 Detector - Shows modal for nginx 404 redirects */}
                  <NotFoundDetector />
                  {/* Dev only: banner when backend returns 503 (e.g. dev server not running) */}
                  <BackendUnavailableBanner />
                  {deferredThirdPartyReady ? <GoogleAnalytics measurementId="G-7Y2PMNCMSD" debugMode={import.meta.env.DEV} /> : null}
                  {deferredThirdPartyReady ? <NewRelicScript /> : null}
                  <AuthProvider>
                  <OverlayAdCoordinatorProvider>
                  <NotificationProvider>
                  <LearningLabProvider>
                  {/* Onboarding Provider - enables guided tours for new users */}
                  <OnboardingProviderWrapper tours={onboardingTours}>
                  <DashboardModuleErrorBoundary moduleKey="learning-lab-banner">
                    <LearningLabBanner />
                  </DashboardModuleErrorBoundary>
                  {/* Consent before AdSense loader so the overlay lane can claim first */}
                  {deferredAdSenseReady ? <HybridConsentManager /> : null}
                  {/* Google AdSense Loader — gated on consent lane free (soft 5s timeout inside loader) */}
                  {deferredAdSenseReady ? <GoogleAdSenseLoader /> : null}
                  {/* Ad Blocking Diagnostics - now controlled via speed dial for admin users in dev mode only */}
                  {/* Removed global render - access via speed dial instead */}
                  {/* Session Expired Message - appears globally when session expires */}
                  <SessionExpiredMessage />
                  {/* Session Warning Modal - appears 5 minutes before expiration */}
                  <SessionWarningModal />
                  <ConditionalChatProvider>
                    <CookieTrackingProvider>
                      <AntiDownloadProvider>
                        {/* Popup Advertisement Component */}
                      <PopupAdvertisement />
                  
                  <div className="app">
                    {/* Studio/AmeriCon tenant hosts (and local ?siteId= preview) own the page outright */}
                    {PUBLIC_TENANT_ROUTER ? (
                      <PublicTenantRouterOutlet />
                    ) : /* Subdomain-based routing - SubdomainAwareContent uses useLocation so it re-renders on nav */
                    subdomainInfo.isSubdomain ? (
                      <SubdomainAwareContent />
                    ) : (
                      <Routes>
                      {/* Debug route */}
                      <Route path="/debug" element={
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            minHeight: '100vh',
                            backgroundColor: 'background.default'
                          }}
                        >
                          <Header />
                          <ProviderAccessBanner />
                          <Box 
                            component="main" 
                            role="main"
                            aria-label="Main content"
                            sx={{ 
                              flexGrow: 1,
                              minHeight: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              py: 3
                            }}
                          >
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                              <div><h1>Debug Route Works!</h1></div>
                            </Box>
                          </Box>
                          <Footer />
                        </Box>
                      } />
                      
                      {/* Student PWA shell: apex `/me/*` — dedicated chrome in PWAWrapper (no site Header/Footer) */}
                      <Route path="/me/*" element={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '100vh',
                            backgroundColor: 'background.default',
                          }}
                        >
                          <ProviderAccessBanner />
                          <Suspense fallback={<PageLoader />}>
                            <MeRoutes />
                          </Suspense>
                        </Box>
                      } />
                      
                      {/* Teach APT PWA shell: apex `/teach/*` — dedicated chrome in PWAWrapper (no site Header/Footer) */}
                      <Route path="/teach/*" element={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '100vh',
                            backgroundColor: 'background.default',
                          }}
                        >
                          <ProviderAccessBanner />
                          <Suspense fallback={<PageLoader />}>
                            <TeachRoutes />
                          </Suspense>
                        </Box>
                      } />
                      
                      {/* Standalone APEX K9™ Quick Menu - Mobile/Tablet Only */}
                      <Route
                        path="/k9-ops/quick-menu"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9OpsQuickMenuPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />

                      {/* Standalone K9 Expense Management - Mobile/Tablet Only */}
                      <Route
                        path="/k9-ops/expense"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9ExpensePage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      
                      {/* APEX K9™ Dashboard - Role-based dashboard for K9 operations */}
                      <Route path="/k9-ops/dashboard" element={
                        <ProtectedRoute>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minHeight: '100vh',
                              backgroundColor: 'background.default'
                            }}
                          >
                            <Header />
                            <ProviderAccessBanner />
                            <Box 
                              component="main" 
                              role="main"
                              aria-label="Main content"
                              sx={{ 
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                py: 3
                              }}
                            >
                              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                                <Suspense fallback={<PageLoader />}>
                                  <K9OpsDashboardPage />
                                </Suspense>
                              </Box>
                            </Box>
                            <Footer />
                          </Box>
                        </ProtectedRoute>
                      } />

                      <Route path="/k9-ops/team" element={
                        <ProtectedRoute>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minHeight: '100vh',
                              backgroundColor: 'background.default'
                            }}
                          >
                            <Header />
                            <ProviderAccessBanner />
                            <Box 
                              component="main" 
                              role="main"
                              aria-label="Main content"
                              sx={{ 
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                py: 3
                              }}
                            >
                              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                                <Suspense fallback={<PageLoader />}>
                                  <K9TeamManagementPage />
                                </Suspense>
                              </Box>
                            </Box>
                            <Footer />
                          </Box>
                        </ProtectedRoute>
                      } />

                      {/* K9 field tools on apex — must be top-level Routes (not only under Layout) or /k9-ops/* leaves Outlet empty */}
                      <Route
                        path="/k9-ops/tracking"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9TrackingPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/tracking/:sessionId/summary"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9TrackingSummaryPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/detection"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9DetectionPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/training"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9TrainingPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/vet"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9VetPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/custody"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9CustodyPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/activity"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9ActivityLogPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      <Route
                        path="/k9-ops/diagnostics"
                        element={
                          <K9OpsApexStandaloneShell>
                            <K9DiagnosticsPage />
                          </K9OpsApexStandaloneShell>
                        }
                      />
                      
                      {/* Backoffice Chat - Agent chat interface */}
                      <Route path="/backoffice/chat" element={
                        <ProtectedRoute requireBackofficeRole={true}>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minHeight: '100vh',
                              backgroundColor: 'background.default'
                            }}
                          >
                            <Header />
                            <ProviderAccessBanner />
                            <Box 
                              component="main" 
                              role="main"
                              aria-label="Main content"
                              sx={{ 
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                py: 3
                              }}
                            >
                              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                                <Suspense fallback={<PageLoader />}>
                                  <BackofficeChatPage />
                                </Suspense>
                              </Box>
                            </Box>
                            <Footer />
                          </Box>
                        </ProtectedRoute>
                      } />
                      
                      {/* Backoffice - Desktop: Regular page, Mobile/Tablet: PWA */}
                      <Route path="/backoffice" element={
                        <ProtectedRoute requireBackofficeRole={true}>
                          <Suspense fallback={<PageLoader />}>
                            <BackofficePage />
                          </Suspense>
                        </ProtectedRoute>
                      } />
                      
                      {/* MFA Verification - standalone route outside Layout */}
                      <Route path="/mfa-verify" element={
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            minHeight: '100vh',
                            backgroundColor: 'background.default'
                          }}
                        >
                          <Header />
                          <ProviderAccessBanner />
                          <Box 
                            component="main" 
                            role="main"
                            aria-label="Main content"
                            sx={{ 
                              flexGrow: 1,
                              minHeight: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              py: 3
                            }}
                          >
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                              <Suspense fallback={<PageLoader />}>
                                <MfaVerifyPage />
                              </Suspense>
                            </Box>
                          </Box>
                          <Footer />
                        </Box>
                      } />
                      
                      {/* MFA Setup - standalone route outside Layout */}
                      <Route path="/mfa-setup" element={
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            minHeight: '100vh',
                            backgroundColor: 'background.default'
                          }}
                        >
                          <Header />
                          <ProviderAccessBanner />
                          <Box 
                            component="main" 
                            role="main"
                            aria-label="Main content"
                            sx={{ 
                              flexGrow: 1,
                              minHeight: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              py: 3
                            }}
                          >
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                              <Suspense fallback={<PageLoader />}>
                                <MfaSetupPage />
                              </Suspense>
                            </Box>
                          </Box>
                          <Footer />
                        </Box>
                      } />
                      
                      {/* 2FA Setup - standalone route outside Layout */}
                      <Route path="/setup-2fa" element={
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            minHeight: '100vh',
                            backgroundColor: 'background.default'
                          }}
                        >
                          <Header />
                          <ProviderAccessBanner />
                          <Box 
                            component="main" 
                            role="main"
                            aria-label="Main content"
                            sx={{ 
                              flexGrow: 1,
                              minHeight: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              py: 3
                            }}
                          >
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                              <Suspense fallback={<PageLoader />}>
                                <Setup2FAPage />
                              </Suspense>
                            </Box>
                          </Box>
                          <Footer />
                        </Box>
                      } />
                      
                      {/* Backoffice routes - outside of main Layout to prevent double headers */}
                      {/* Logout confirmation - no auth required */}
                      <Route path="/backoffice/logout-confirmation" element={
                        <Suspense fallback={<PageLoader />}>
                          <LogoutConfirmationPage />
                        </Suspense>
                      } />
                      <Route path="/logout-confirmation" element={
                        <Suspense fallback={<PageLoader />}>
                          <LogoutConfirmationPage />
                        </Suspense>
                      } />
                      
                      <Route path="/backoffice/healthcare" element={
                        <ProtectedRoute requireBackofficeRole={true}>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minHeight: '100vh',
                              backgroundColor: 'background.default'
                            }}
                          >
                            <Header />
                            <ProviderAccessBanner />
                            <Box 
                              component="main" 
                              role="main"
                              aria-label="Main content"
                              sx={{ 
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                py: 3
                              }}
                            >
                              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                                <Suspense fallback={<PageLoader />}>
                                  <HealthcareAdminPage />
                                </Suspense>
                              </Box>
                            </Box>
                            <Footer />
                          </Box>
                        </ProtectedRoute>
                      } />
                      <Route path="/backoffice/*" element={
                        <ProtectedRoute requireBackofficeRole={true}>
                          <Suspense fallback={<PageLoader />}>
                            <BackofficePage />
                          </Suspense>
                        </ProtectedRoute>
                      } />
                      
                      {/* Backoffice routes removed from main domain to prevent conflicts with public routes */}
                      {/* Backoffice access should only be through /backoffice/* paths */}

                      {/* Embed routes - outside of main Layout to show only the calendar. */}
                      <Route
                        path="/embed/calendar/:agencyId"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <EmbedCalendarPage />
                          </Suspense>
                        }
                      />

                      {/* Travel: dedicated route so /travel is never captured by `/:slug` → EventSlugRouter (guests + logged-in). */}
                      <Route path="/travel" element={<Layout />}>
                        <Route
                          index
                          element={
                            <Suspense fallback={<PageLoader />}>
                              <TravelAccommodationsPage />
                            </Suspense>
                          }
                        />
                        {/* GSA per diem lookup; renders NotFound when the feature flag is off. */}
                        <Route
                          path="per-diem"
                          element={
                            <Suspense fallback={<PageLoader />}>
                              <PerDiemCalculatorPage />
                            </Suspense>
                          }
                        />
                      </Route>

                      <Route path="/" element={<Layout />}>
                        <Route index element={
                          <ErrorBoundary>
                            <Suspense fallback={<HomeRouteFallback />}>
                              <HomePage />
                            </Suspense>
                          </ErrorBoundary>
                        } />
                        <Route path="contact" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ContactPage />
                          </Suspense>
                        } />
                        <Route path="support" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SupportTicketPage />
                          </Suspense>
                        } />
                        <Route path="signup/choose" element={
                          <Suspense fallback={<PageLoader />}>
                            <SignupChoosePage />
                          </Suspense>
                        } />
                        <Route path="signup" element={
                          <Suspense fallback={<PageLoader />}>
                            <SignupPage />
                          </Suspense>
                        } />
                        <Route path="signup-success" element={
                          <Suspense fallback={<PageLoader />}>
                            <SignupSuccessPage />
                          </Suspense>
                        } />
                        <Route path="payment-success" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <PaymentSuccessPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="login" element={<LoginPage />} />
                        <Route path="auth/login" element={<LoginPage />} />
                        <Route
                          path="officeapt/auth"
                          element={<OfficeaptAuthBridgePage />}
                        />
                        <Route
                          path="agent/claim"
                          element={
                            <Suspense fallback={<PageLoader />}>
                              <AgentClaimPage />
                            </Suspense>
                          }
                        />
                        <Route path="forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="reset-password" element={<ResetPasswordPage />} />
                        <Route path="change-password" element={<ChangePasswordPage />} />
                        <Route path="logout-confirmation" element={
                        <Suspense fallback={<PageLoader />}>
                          <LogoutConfirmationPage />
                        </Suspense>
                      } />
                        <Route path="verify-location/:token" element={
                          <Suspense fallback={<PageLoader />}>
                            <VerifyLocationPage />
                          </Suspense>
                        } />
                        <Route path="verify-email/:token" element={
                          <Suspense fallback={<PageLoader />}>
                            <VerifyEmailPage />
                          </Suspense>
                        } />
                        <Route path="verify/:code?" element={
                          <Suspense fallback={<PageLoader />}>
                            <VerifyCertificatePage />
                          </Suspense>
                        } />
                        <Route
                          path="settings/security"
                          element={<Navigate to="/dashboard/settings/security" replace />}
                        />
                        <Route path="events" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <EventsPage />
                          </Suspense>
                        } />
                        <Route path="alerts" element={
                          // Short marketing landing — avoid the tall CLS shell fallback.
                          <Suspense fallback={<PageLoader />}>
                            <TrainingAlertsLandingPage />
                          </Suspense>
                        } />
                        <Route path="for-officers" element={
                          <Suspense fallback={<PageLoader />}>
                            <ForOfficersLandingPage />
                          </Suspense>
                        } />
                        <Route path="for-professionals" element={
                          <Suspense fallback={<PageLoader />}>
                            <ForProfessionalsLandingPage />
                          </Suspense>
                        } />
                        <Route path="write-for-apt" element={
                          <Suspense fallback={<PageLoader />}>
                            <WriteForAptLandingPage />
                          </Suspense>
                        } />
                        <Route path="scorecard/agency-training-readiness" element={
                          <Suspense fallback={<PageLoader />}>
                            <AgencyTrainingReadinessPage />
                          </Suspense>
                        } />
                        <Route path="scorecard/agency-training-readiness/r/:token" element={
                          <Suspense fallback={<PageLoader />}>
                            <AgencyTrainingReadinessResultsPage />
                          </Suspense>
                        } />
                        <Route path="tools/tcole-psr" element={
                          <Suspense fallback={<PageLoader />}>
                            <TcolePsrLandingPage />
                          </Suspense>
                        } />
                        <Route path="tac-shop" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TacShopPage />
                          </Suspense>
                        } />
                        <Route path="tac-shop/product/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TacShopProductDetailPage />
                          </Suspense>
                        } />
                        <Route path="tac-shop/cart" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TacShopCartPage />
                          </Suspense>
                        } />
                        <Route path="tac-shop/checkout" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TacShopCheckoutPage />
                          </Suspense>
                        } />
                        <Route path="tac-shop/order/:orderNumber" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TacShopOrderPage />
                          </Suspense>
                        } />
                        <Route path="secondary-employment/for-employers" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SecondaryEmploymentForEmployersPage />
                          </Suspense>
                        } />
                        <Route path="secondary-employment" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SecondaryEmploymentHomePage />
                          </Suspense>
                        } />
                        <Route path="secondary-employment/listings/:id" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SecondaryEmploymentListingDetailPage />
                          </Suspense>
                        } />
                        <Route path="secondary-employment/employers/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SecondaryEmploymentEmployerPublicPage />
                          </Suspense>
                        } />
                        <Route path="secondary-employment/dashboard" element={
                          <Suspense fallback={<PageLoader />}>
                            <SecondaryEmploymentDashboardPage />
                          </Suspense>
                        } />
                        {/* TactiCore web app + public product page (gated by TactiCoreRootRouter) */}
                        <Route path="tacticore" element={
                          <Suspense fallback={<PageLoader />}>
                            <TactiCoreRootRouter />
                          </Suspense>
                        } />
                        <Route
                          path="tacticore/download"
                          element={
                            <ProtectedRoute useRoutePolicies={false}>
                              <Suspense fallback={<PageLoader />}>
                                <TactiCoreTexasDownloadPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="tacticore/lesson-plans" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <LessonPlansManager />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/lesson-plans/new" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <LessonPlanFormPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/lesson-plans/:id/edit" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <LessonPlanFormPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/lesson-plans/:id" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <LessonPlanViewPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/library" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <LessonPlanLibraryManager />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/exams" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <ExamsManager />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/exams/new" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <ExamFormPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/exams/:id/edit" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <ExamFormPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/exams/:id" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <ExamFormPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/documents" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <DocumentsLibraryManager />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/references" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <ReferencesLibraryManager />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/license-management" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <LicenseManagementPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/instructor-bios" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <InstructorBiosContent />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="tacticore/activity-logs" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <TactiCoreActivityLogsManager />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Legacy short paths → `/tacticore/*` */}
                        <Route path="download" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="lesson-plans/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="exams/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="documents/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="references/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="library/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="license-management/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="instructor-bios/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="activity-logs/*" element={<RedirectShortPathToTacticoreApp />} />
                        <Route path="faq" element={<FAQPage />} />
                        <Route path="agencies/org-chart" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <AgencyOrgChartPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="agencies/:agencyId/org-chart" element={
                          <Suspense fallback={<PageLoader />}>
                            <AgencyOrgChartLegacyRedirect />
                          </Suspense>
                        } />
                        <Route path="agencies" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <AgenciesPage />
                          </Suspense>
                        } />
                        <Route path="agencies/:id" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <AgencyDetailPage />
                          </Suspense>
                        } />
                        <Route path="agencies/:id/recruiting/jobs/:jobSlug" element={
                          <Suspense fallback={<PageLoader />}>
                            <AgencyRecruitingJobDetailPage />
                          </Suspense>
                        } />
                        {/* Before agency/:id — :id would capture "org-chart" and SingularEntityRedirect → /agencies/org-chart */}
                        <Route path="agency/org-chart" element={
                          <Suspense fallback={<PageLoader />}>
                            <AgencyOrgChartLegacyRedirect />
                          </Suspense>
                        } />
                        <Route path="agency/:id" element={<SingularEntityRedirect pluralBase="agencies" />} />
                        <Route path="favorites" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <UserFavoritesPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="blog" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <BlogPage />
                          </Suspense>
                        } />
                        <Route path="blog/contribute" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <BlogContributePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="blog/manage" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <BlogManagementPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="blog/manage/new" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <BlogPostEditorPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="blog/manage/:id/edit" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <BlogPostEditorPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="blog/manage/:id/preview" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <BlogPostPreviewPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="contribute" element={<Navigate to="/blog/contribute" replace />} />
                        <Route path="manage" element={<Navigate to="/blog/manage" replace />} />
                        <Route path="manage/new" element={<Navigate to="/blog/manage/new" replace />} />
                        <Route path="manage/:id/edit" element={<RedirectBlogManageEdit />} />
                        <Route path="manage/:id/preview" element={<RedirectBlogManagePreview />} />
                        <Route path="blog/series" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <BlogSeriesListPage />
                          </Suspense>
                        } />
                        <Route path="blog/series/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <BlogSeriesDetailPage />
                          </Suspense>
                        } />
                        <Route path="blog/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <BlogPostDetailPage />
                          </Suspense>
                        } />
                        <Route path="knowledge" element={
                          <Suspense fallback={<PageLoader />}>
                            <KnowledgeBaseHomePage />
                          </Suspense>
                        } />
                        <Route path="knowledge/questions" element={
                          <Suspense fallback={<PageLoader />}>
                            <KnowledgeBaseQuestionsPage />
                          </Suspense>
                        } />
                        <Route path="knowledge/questions/confirm" element={
                          <Suspense fallback={<PageLoader />}>
                            <KnowledgeBaseQuestionConfirmPage />
                          </Suspense>
                        } />
                        <Route path="knowledge/questions/:questionSlug" element={
                          <Suspense fallback={<PageLoader />}>
                            <KnowledgeBaseQuestionDetailPage />
                          </Suspense>
                        } />
                        <Route path="authors" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <AuthorsPage />
                          </Suspense>
                        } />
                        <Route path="authors/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <BlogAuthorPage />
                          </Suspense>
                        } />
                        <Route path="faq/:slug" element={
                          <Suspense fallback={<PageLoader />}>
                            <FAQDetailPage />
                          </Suspense>
                        } />
                        {/* Agency detail lives on apex `/agencies/:slug` (legacy agency.* host 301s here) */}
                        <Route path="instructor/:slug" element={<SingularEntityRedirect pluralBase="instructors" />} />
                        <Route path="instructors/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <InstructorSlugPage />
                          </Suspense>
                        } />
                        <Route path="instructors" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <InstructorsPage />
                          </Suspense>
                        } />
                        <Route path="venue/:slug" element={<SingularEntityRedirect pluralBase="venues" />} />
                        <Route path="venue/detail/:slug" element={<LegacyDetailPathRedirect targetBase="venues" />} />
                        <Route path="event/detail/:slug" element={<LegacyDetailPathRedirect targetBase="events" />} />
                        <Route path="blog/detail/:slug" element={<LegacyDetailPathRedirect targetBase="blog" />} />
                        <Route path="agency/detail/:slug" element={<LegacyDetailPathRedirect targetBase="agencies" />} />
                        <Route path="venues/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VenueSlugPage />
                          </Suspense>
                        } />
                        <Route path="event/:slug" element={<SingularEntityRedirect pluralBase="events" />} />
                        <Route path="events/in/:state/topic/:topic" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <EventsPage />
                          </Suspense>
                        } />
                        <Route path="events/in/:state" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <EventsPage />
                          </Suspense>
                        } />
                        <Route path="events/topic/:topic" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <EventsPage />
                          </Suspense>
                        } />
                        <Route path="events/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <EventSlugPage />
                          </Suspense>
                        } />
                        {/* More-specific /events/:slug/* must be siblings of events/:slug (dash.* mirrors) */}
                        <Route path="events/:slug/speaker-invite/:token" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ConferenceSpeakerInvitePage />
                          </Suspense>
                        } />
                        <Route path="events/:slug/registration/:token" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ConferenceRegistrationManagePage />
                          </Suspense>
                        } />
                        <Route path="events/:slug/check-in/:token" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ConferenceCheckInPage />
                          </Suspense>
                        } />
                        <Route path="conferences" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ConferencesHubPage />
                          </Suspense>
                        } />
                        <Route path="conferences/certificates/:code" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ConferenceCertificateVerifyPage />
                          </Suspense>
                        } />
                        <Route path="about" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <AboutPage />
                          </Suspense>
                        } />
                        <Route
                          path="standards"
                          element={<Navigate to="/standards/tics" replace />}
                        />
                        <Route path="standards/tics" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TicsStandardsPage />
                          </Suspense>
                        } />
                        <Route path="standards/tics/white-paper" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TicsWhitePaperPage />
                          </Suspense>
                        } />
                        <Route path="standards/tics/specification" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TicsSpecificationOverviewPage />
                          </Suspense>
                        } />
                        <Route path="upcoming-features" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <UpcomingFeaturesPage />
                          </Suspense>
                        } />
                        <Route path="venues" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VenuesPage />
                          </Suspense>
                        } />
                        {/* Canonical restaurant listing/detail (matches getRestaurantDetailUrl / server 301 from /eat/restaurants*) */}
                        <Route path="restaurants" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <RestaurantsPage />
                          </Suspense>
                        } />
                        <Route path="restaurants/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <RestaurantDetailPage />
                          </Suspense>
                        } />
                        <Route path="eat" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <LEODiningPage />
                          </Suspense>
                        } />
                        <Route path="eat/features" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <RestaurantFeaturesPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="eat/restaurants" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <RestaurantsPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="eat/restaurants/:slug" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <RestaurantDetailPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="eat/search" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <RestaurantSearch />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="eat/recommendations" element={
                          <ProtectedRoute>
                            <RestaurantRecommendations />
                          </ProtectedRoute>
                        } />
                        {/* Public: anonymous suggestion form (matches eat subdomain /suggest) */}
                        <Route path="eat/suggest" element={<RestaurantSuggestionForm />} />
                        <Route path="premium" element={
                          <Suspense fallback={<PageLoader />}>
                            <PremiumPage />
                          </Suspense>
                        } />
                        <Route path="services" element={
                          <Suspense fallback={<PageLoader />}>
                            <ServicesPage />
                          </Suspense>
                        } />
                        <Route path="sponsors" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SponsorsPage />
                          </Suspense>
                        } />
                        <Route path="affiliates" element={
                          <Suspense fallback={<PageLoader />}>
                            <AffiliatesPage />
                          </Suspense>
                        } />
                        <Route path="vendors" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VendorsPage />
                          </Suspense>
                        } />
                        <Route path="vendors/apply/success" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VendorApplySuccessPage />
                          </Suspense>
                        } />
                        <Route path="vendors/apply" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VendorApplyPage />
                          </Suspense>
                        } />
                        <Route path="vendors/join" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VendorJoinPage />
                          </Suspense>
                        } />
                        <Route path="vendors/:slug/products/:productSlug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VendorProductDetailPage />
                          </Suspense>
                        } />
                        <Route path="vendors/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <VendorDetailPage />
                          </Suspense>
                        } />
                        <Route path="vendor-console" element={
                          <Suspense fallback={<PageLoader />}>
                            <VendorConsolePage />
                          </Suspense>
                        } />
                        <Route path="training-provider-services" element={
                          <Suspense fallback={<PageLoader />}>
                            <TrainingProviderServicesPage />
                          </Suspense>
                        } />
                        <Route path="list-training/:stateSlug" element={
                          <Suspense fallback={<PageLoader />}>
                            <ListTrainingInStatePage />
                          </Suspense>
                        } />
                        <Route path="provider-signup" element={
                          <Suspense fallback={<PageLoader />}>
                            <ProviderSignupPage />
                          </Suspense>
                        } />
                        <Route path="provider-signup/complete" element={
                          <Suspense fallback={<PageLoader />}>
                            <ProviderProfileCompletePage />
                          </Suspense>
                        } />
                        <Route path="outreach/correct" element={
                          <Suspense fallback={<PageLoader />}>
                            <OutreachCorrectPage />
                          </Suspense>
                        } />
                        <Route path="employer-signup" element={
                          <Suspense fallback={<PageLoader />}>
                            <EmployerSignupPage />
                          </Suspense>
                        } />
                        <Route path="professional-signup" element={
                          <Suspense fallback={<PageLoader />}>
                            <ProfessionalSignupPage />
                          </Suspense>
                        } />
                        <Route path="professionals" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <ProfessionalsDirectoryPage />
                          </Suspense>
                        } />
                        <Route path="people" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <PeopleDirectoryPage />
                          </Suspense>
                        } />
                        <Route path="people/unlist" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <PeopleUnlistConfirmPage />
                          </Suspense>
                        } />
                        <Route path="people/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <PersonProfilePage />
                          </Suspense>
                        } />
                        <Route path="careers" element={
                          <Suspense fallback={<PageLoader />}>
                            <CareersPage />
                          </Suspense>
                        } />
                        <Route path="leaderboard" element={
                          <Suspense fallback={<PageLoader />}>
                            <LeaderboardPage />
                          </Suspense>
                        } />
                        <Route path="privacy" element={
                          <Suspense fallback={<PageLoader />}>
                            <PrivacyPage />
                          </Suspense>
                        } />
                        <Route path="terms" element={
                          <Suspense fallback={<PageLoader />}>
                            <TermsPage />
                          </Suspense>
                        } />
                        <Route path="security" element={
                          <Suspense fallback={<PageLoader />}>
                            <SecurityPage />
                          </Suspense>
                        } />
                        <Route path="cookie-policy" element={
                          <Suspense fallback={<PageLoader />}>
                            <CookiePolicyPage />
                          </Suspense>
                        } />
                        <Route path="gdpr-compliance" element={
                          <Suspense fallback={<PageLoader />}>
                            <GDPRCompliancePage />
                          </Suspense>
                        } />
                        <Route path="data-request" element={
                          <Suspense fallback={<PageLoader />}>
                            <DataRequestPage />
                          </Suspense>
                        } />
                        <Route path="tx-ramp-compliance" element={
                          <Suspense fallback={<PageLoader />}>
                            <TXRampCompliancePage />
                          </Suspense>
                        } />
                        <Route path="preferences" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <UserPreferencesPanel />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="analytics" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <EnhancedAnalytics />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="courses" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CoursesPage />
                          </Suspense>
                        } />
                        <Route path="courses/compare" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CourseCatalogComparePage />
                          </Suspense>
                        } />
                        <Route path="courses/requirements" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <RequirementsDirectoryPage />
                          </Suspense>
                        } />
                        <Route path="courses/domains" element={<Navigate to="/courses" replace />} />
                        <Route path="courses/subjects" element={<Navigate to="/courses" replace />} />
                        <Route path="courses/families" element={<Navigate to="/courses" replace />} />
                        <Route
                          path="courses/domains/:domainSlug/subjects/:subjectSlug/families/:familySlug"
                          element={
                            <Suspense fallback={<PublicShellFallback />}>
                              <CoursesPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="courses/domains/:domainSlug/subjects/:subjectSlug"
                          element={
                            <Suspense fallback={<PublicShellFallback />}>
                              <CoursesPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="courses/domains/:domainSlug"
                          element={
                            <Suspense fallback={<PublicShellFallback />}>
                              <CoursesPage />
                            </Suspense>
                          }
                        />
                        <Route path="c/:tciRef" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CourseTciRedirectPage />
                          </Suspense>
                        } />
                        <Route path="courses/topic/:topic" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CoursesPage />
                          </Suspense>
                        } />
                        <Route path="courses/:id/events" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CourseCatalogEventsArchivePage />
                          </Suspense>
                        } />
                        <Route path="courses/:id/requirements" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CourseCatalogRequirementsHubPage />
                          </Suspense>
                        } />
                        <Route path="courses/:id/lesson-plans" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CourseCatalogLessonPlansHubPage />
                          </Suspense>
                        } />
                        <Route path="courses/:id" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            {isViteFlagEnabled('VITE_COURSE_DIRECTORY_ENABLED', import.meta.env.VITE_COURSE_DIRECTORY_ENABLED) ? (
                              <CourseCatalogDetailPage />
                            ) : (
                              <CourseDetailPage />
                            )}
                          </Suspense>
                        } />
                        <Route path="training-requirements/:id" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <TrainingRequirementDetailPage />
                          </Suspense>
                        } />
                        <Route path="credentials" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CredentialCatalogPage />
                          </Suspense>
                        } />
                        <Route path="credentials/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CredentialDetailPage />
                          </Suspense>
                        } />
                        <Route path="certification" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <CertificationPage />
                          </Suspense>
                        } />
                        <Route path="support" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <SupportPage />
                          </Suspense>
                        } />
                        <Route path="docs" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <DocsPage />
                          </Suspense>
                        } />
                        {/* Gallery Routes */}
                        <Route path="gallery" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <GalleryPage />
                          </Suspense>
                        } />
                        <Route path="upload" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <GalleryUploadPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="gallery/photo/:id" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <GalleryPhotoDetailPage />
                          </Suspense>
                        } />
                        <Route path="gallery/photo/:slug" element={
                          <Suspense fallback={<PublicShellFallback />}>
                            <GalleryPhotoDetailPage />
                          </Suspense>
                        } />
                        {/* Specific dashboard routes - must come before general dashboard route */}
                        <Route path="dashboard/instructor-bio/:instructorId" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <InstructorBioManagement />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/instructor-bio" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <InstructorBioManagement />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/profile" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <ProfilePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route
                          path="dashboard/people-profile"
                          element={<Navigate to="/dashboard/settings?tab=profile" replace />}
                        />
                        <Route path="dashboard/settings" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <ProfilePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/agency-profile" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <AgencyProfilePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Mirror dash.* coordinator pages on apex (else * → DashboardPage) */}
                        <Route path="dashboard/coordinator/office-submissions" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <AgencyOfficeSubmissionsPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/coordinator/training-plan" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <AgencyTrainingPlanPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/studio" element={
                          <AptStudioLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AptStudioWorkspaceLayout />
                            </Suspense>
                          </AptStudioLaunchProtected>
                        }>
                          <Route index element={<AptStudioDashboardPage />} />
                          <Route path="pages" element={<AptStudioPagesListPage />} />
                          <Route path="brand" element={<AptStudioBrandPage />} />
                          <Route path="shop" element={<AptStudioShopPage />} />
                          <Route path="blog" element={<AptStudioBlogPage />} />
                          <Route path="domains" element={<AptStudioDomainPage />} />
                          <Route path="billing" element={<AptStudioBillingPage />} />
                          <Route path="white-label" element={<AptStudioWhiteLabelPage />} />
                          <Route path="settings" element={<AptStudioOrgSettingsPage />} />
                        </Route>
                        <Route path="dashboard/studio/accept-invite" element={
                          <StudioAmericonDashboardLaunchGate product="apt_studio">
                            <Suspense fallback={<PageLoader />}>
                              <AptStudioAcceptInvitePage />
                            </Suspense>
                          </StudioAmericonDashboardLaunchGate>
                        } />
                        <Route path="dashboard/studio/edit" element={
                          <AptStudioLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AptStudioEditRedirect />
                            </Suspense>
                          </AptStudioLaunchProtected>
                        } />
                        <Route path="dashboard/studio/pages/:pageId/edit" element={
                          <AptStudioLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AptStudioPageEditorPage />
                            </Suspense>
                          </AptStudioLaunchProtected>
                        } />
                        <Route path="dashboard/studio/builder/:pageId" element={
                          <AptStudioLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <StudioBuilderRoute />
                            </Suspense>
                          </AptStudioLaunchProtected>
                        } />
                        <Route path="dashboard/americon" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConDashboardPage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/orgs/:orgId/editions" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConEditionListPage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/orgs/:orgId/editions/:editionId/site" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConEditionSitePage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/orgs/:orgId/editions/:editionId/content" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConEditionContentPage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/orgs/:orgId/domains" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConDomainPage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/orgs/:orgId/settings" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConOrgSettingsPage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/orgs/:orgId/billing" element={
                          <AmeriConLaunchProtected>
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConBillingPage />
                            </Suspense>
                          </AmeriConLaunchProtected>
                        } />
                        <Route path="dashboard/americon/accept-invite" element={
                          <StudioAmericonDashboardLaunchGate product="americon">
                            <Suspense fallback={<PageLoader />}>
                              <AmeriConAcceptInvitePage />
                            </Suspense>
                          </StudioAmericonDashboardLaunchGate>
                        } />
                        <Route path="dashboard/coordinator-conference-create" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorConferenceCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/coordinator-event-create" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="dashboard/training-manager-event-create" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Training Manager Event Management */}
                        <Route path="dashboard/event-management" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <TrainingManagerEventManagementPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="coordinator-event-create" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="coordinator-event-edit/:eventId" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="coordinator-event-manage/:eventId" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventManagePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="coordinator-conference-create" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorConferenceCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="coordinator-conference-edit/:eventId" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorConferenceCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="coordinator-conference-manage/:eventId" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorConferenceManagePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="events/:eventId/edit" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="events/:eventId/roster" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <EventRosterPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="training-manager-event-create" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                              <CoordinatorEventCreatePage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        {/* General dashboard route - must come after specific routes */}
                        <Route path="dashboard" element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoader />}>
                            <DashboardPage />
                            </Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Before :slug so /k9-ops is not treated as an event slug */}
                        <Route path="k9-ops" element={
                          <Suspense fallback={<PageLoader />}>
                            <K9OpsPage />
                          </Suspense>
                        } />
                        <Route path="apt-studio" element={
                          <Suspense fallback={<PageLoader />}>
                            <AptStudioPage />
                          </Suspense>
                        } />
                        <Route path="chatapt" element={
                          <Suspense fallback={<PageLoader />}>
                            <ChatAptPage />
                          </Suspense>
                        } />
                        <Route path="americon" element={
                          <Suspense fallback={<PageLoader />}>
                            <AmeriConPage />
                          </Suspense>
                        } />
                        <Route path="myapt" element={
                          <Suspense fallback={<PageLoader />}>
                            <MyAptPage />
                          </Suspense>
                        } />
                        <Route path="teachapt" element={
                          <Suspense fallback={<PageLoader />}>
                            <TeachAptPage />
                          </Suspense>
                        } />
                        {/* Backoffice pages that must not fall through to EventSlugRouter → homepage */}
                        <Route path="seo-content" element={<Navigate to="/backoffice/seo-content" replace />} />
                        <Route path="agency-enrichment" element={<Navigate to="/backoffice/agency-enrichment" replace />} />
                        <Route path="cloudflare" element={<Navigate to="/backoffice/cloudflare" replace />} />
                        <Route path=":slug" element={
                          <Suspense fallback={<PageLoader />}>
                            <EventSlugRouter />
                          </Suspense>
                        } />
                      </Route>
              {/* Reports */}
              <Route path="reports/events" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <EventReportsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="reports/performance" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <PerformanceReportsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="reports/sign-in-sheets" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <SignInSheetsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="debug/auth-test" element={
                <Suspense fallback={<PageLoader />}>
                  <AuthTestComponent />
                </Suspense>
              } />
              <Route path="register" element={
                <Suspense fallback={<PageLoader />}>
                  <SignupPage />
                </Suspense>
              } />
              <Route path="setup-password" element={
                <Suspense fallback={<PageLoader />}>
                  <SetupPasswordPage />
                </Suspense>
              } />
              <Route path="admin/data-explorer" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <DataExplorerPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/settings" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <SystemSettingsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="events/:eventId/edit" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                  <CoordinatorEventCreatePage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="events/:eventId/roster" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                  <EventRosterPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="events/:eventId/instructor" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                  <InstructorEventPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="events/:eventId/student" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                  <StudentEventPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="roster-management" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                  <RosterManagementPage />
                  </Suspense>
                </ProtectedRoute>
              } />
                    <Route path="events/create" element={
                      <Suspense fallback={<PageLoader />}>
                        <EventCreateRedirect />
                      </Suspense>
                    } />
                    {/* Supervisor routes */}
                    <Route path="supervisor/members" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <SupervisorMembersPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="supervisor/members/:id" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <SupervisorMemberDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                      <Route path="calendar-fullscreen" element={
                      <Suspense fallback={<PageLoader />}>
                        <CalendarFullScreenPage />
                      </Suspense>
                    } />
                    <Route path="dashboard/instructors" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <InstructorsPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="dashboard/instructors/:instructorId" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <InstructorBioPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    
                    {/* We Salute You Routes */}
                    <Route path="we-salute-you" element={
                      <Suspense fallback={<PublicShellFallback />}>
                        <WeSaluteYouPage />
                      </Suspense>
                    } />
                    <Route path="we-salute-you/:slug" element={
                      <Suspense fallback={<PublicShellFallback />}>
                        <WeSaluteYouDetailPage />
                      </Suspense>
                    } />
                    <Route path="we-salute-you/submit" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <WeSaluteYouSubmissionForm />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="profile" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <ProfilePage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="settings" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <ProfilePage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    
                    {/* Messages Route */}
                    <Route path="messages" element={
                      <ProtectedRoute>
                        <PlatformPageWrapper>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minHeight: '100vh',
                              backgroundColor: 'background.default'
                            }}
                          >
                            <Header />
                            <ProviderAccessBanner />
                            <Box 
                              component="main" 
                              role="main"
                              aria-label="Main content"
                              sx={{ 
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                py: 3
                              }}
                            >
                              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                                <Suspense fallback={<PageLoader />}>
                                  <MessagesPage />
                                </Suspense>
                              </Box>
                            </Box>
                            <Footer />
                          </Box>
                        </PlatformPageWrapper>
                      </ProtectedRoute>
                    } />
                    
                    {/* Notifications Route */}
                    <Route path="notifications" element={
                      <ProtectedRoute>
                        <PlatformPageWrapper>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              minHeight: '100vh',
                              backgroundColor: 'background.default'
                            }}
                          >
                            <Header />
                            <ProviderAccessBanner />
                            <Box 
                              component="main" 
                              role="main"
                              aria-label="Main content"
                              sx={{ 
                                flexGrow: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                py: 3
                              }}
                            >
                              <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', px: AGENCY_SHELL_HORIZONTAL_PX }}>
                                <Suspense fallback={<PageLoader />}>
                                  <NotificationsPage />
                                </Suspense>
                              </Box>
                            </Box>
                            <Footer />
                          </Box>
                        </PlatformPageWrapper>
                      </ProtectedRoute>
                    } />
                    
                    {/* Coordinator Routes */}
                    <Route path="coordinator/events/create" element={
                      <Suspense fallback={<PageLoader />}>
                        <EventCreateRedirect />
                      </Suspense>
                    } />
                    
                    {/* Coordinator Event Create Route */}
                    <Route path="coordinator-event-create" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    
                    {/* Training Manager Routes */}
                    <Route path="training-manager-event-create" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <CoordinatorEventCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    
                    {/* Admin routes */}
                    <Route path="admin/*" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                        <AdminLayout />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    
                    {/* Catch-all route - must be last - shows dashboard for authenticated users */}
                    <Route path="*" element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <DashboardPage />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                  </Routes>
                  )}
                  </div>
                  
                  {/* 404 SEO override - renders after route content so 404 meta wins when ?404=true */}
                  <NotFoundSEOOverride />
                  
                  {/* Floating Chat Widget - deferred on `/` until openChatWidget (LCP) */}
                  {subdomainInfo.subdomain !== SUBDOMAINS.K9
                    && subdomainInfo.subdomain !== SUBDOMAINS.BACKOFFICE
                    && subdomainInfo.subdomain !== SUBDOMAINS.CHAT
                    && subdomainInfo.subdomain !== SUBDOMAINS.ME
                    && subdomainInfo.subdomain !== SUBDOMAINS.TEACH && (
                    <DeferredSupportChatWidget />
                  )}
                  
                  {/* Impersonation Banner - Floating button to exit impersonation */}
                  <ImpersonationBanner />
                  
                </AntiDownloadProvider>
              </CookieTrackingProvider>
                </ConditionalChatProvider>
                </OnboardingProviderWrapper>
                  </LearningLabProvider>
                  </NotificationProvider>
                  </OverlayAdCoordinatorProvider>
            </AuthProvider>
          </Router>
        </HelmetProvider>
            </SessionWarningProvider>
          </SessionExpiredProvider>
        </MuiThemeContextProvider>
      </ErrorBoundary>
      </ReduxProvider>
    </ReactProvider>
  );
};

export default App;
 
